import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts'
import { Marker, Popup, Polyline, Polygon, Circle, useMap, useMapEvents } from 'react-leaflet'
import L from 'leaflet'
import { LeafletMap, FALLBACK_CENTER } from '../components/Map'
import { clamp01, devicePulse, reservoirPulse, pumpPulse, pulseColor, pulseTextColor, type PulseCategory } from '../utils/pulseCategory'
import {
  scadaApi,
  openScadaStream,
  type NodeType,
  type PumpStatus,
  type ReservoirStatus,
  type ReservoirHistoryPoint,
  type PumpHistoryPoint,
  type ScadaEvent,
  type ScadaDevice,
  type ScadaZone,
  type ScadaBoundaryPoint,
  type ScadaAccessPoint,
  type EdgeNodeSummary,
  type ScadaZoneLink,
  type ScadaZoneLinkPoint,
  type PathPointType,
  type ZoneCategory,
  type ZoneFlowStatus,
  type PumpReservoirLink,
  type ModbusReading,
  type ModbusRegisterType,
  type DeviceCommErrorCategory,
  type StatusSeverity,
} from '../api/scada'
import { zonesApi, ZONE_TYPE_LABEL, ZONE_TYPE_COLOR, type Zone } from '../api/zones'
import { edgeConnStatus, EDGE_STATUS_DOT, EDGE_STATUS_PILL } from '../utils/edgeStatus'
import { useAuthStore } from '../store/authStore'
import { Badge, severityTone } from '../components/Badge'
import { FaultNotice, faultText, faultMarkerBadgeHtml } from '../components/FaultBadge'
import { DockedMapEditor, type DockedMapEditorHandle } from '../components/map-edit/DockedMapEditor'
import { useDeviceDetailModal } from '../hooks/useDeviceDetailModal'

// Literal accent colors — used only where Recharts/SVG need a string prop, not page chrome.
// "active" = Good/Active Blue (guide §4A: pipes/nodes stay this color when
// safe; only alarms pulse orange) — not Point of Interest Blue, which the
// guide reserves for location markers whose color signals place, not state.
const ACCENT = {
  active: 'var(--color-brand-cyan)',
  orange: 'var(--color-brand-orange)',
  amber:  '#F59E0B', // generic Tailwind amber-500, not a brand token — left as-is
}

// ─── helpers ──────────────────────────────────────────────────────────────────

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString('sr-Latn-RS', {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
}

function fmtAgo(iso: string | null) {
  if (!iso) return 'Nema podataka'
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000)
  if (s < 5)    return 'Upravo'
  if (s < 60)   return `${s}s`
  if (s < 3600) return `${Math.floor(s / 60)} min`
  return `${Math.floor(s / 3600)} h`
}


function fmtCountdown(minutes: number | null) {
  if (minutes === null) return null
  if (minutes === 0)    return '⚠ Kritično'
  if (minutes < 60)     return `${minutes} min`
  const h = Math.floor(minutes / 60), m = minutes % 60
  return m > 0 ? `${h} h ${m} min` : `${h} h`
}

// Only the two categories the user actually walked through while testing get
// a named Serbian label — the other 10 pymodbus exception names are exact
// protocol-error names that fall back to a generic "greška: {value}" label
// rather than translating all of them for a first pass.
const COMM_ERROR_LABEL: Partial<Record<DeviceCommErrorCategory, string>> = {
  IllegalAddress: 'Nepostojeća adresa',
  no_response:    'Bez odgovora',
}

function commErrorLabel(category: DeviceCommErrorCategory): string {
  return COMM_ERROR_LABEL[category] ?? `Greška: ${category}`
}

const ZONE_CAT_LABEL: Record<ZoneCategory, string> = {
  well:         'Bunar',
  reservoir:    'Rezervoar',
  distribution: 'Distribucija',
  source:       'Izvor',
}

const SCADA_CAT_ORDER: ZoneCategory[] = ['well', 'reservoir', 'distribution', 'source']

const NODE_TYPE_LABEL: Record<string, string> = {
  pump:             'Crpna stanica (pumpa)',
  reservoir:        'Rezervoar',
  flow_meter:       'Merač protoka',
  pressure_sensor:  'Senzor pritiska',
  level_sensor:     'Senzor nivoa',
  valve:            'Ventil',
}

const UNIT_OPTIONS = [
  { value: 'lps',  label: 'L/s — protok' },
  { value: 'm',    label: 'm — dubina/nivo' },
  { value: 'm3',   label: 'm³ — zapremina' },
  { value: 'kpa',  label: 'kPa — pritisak' },
  { value: 'bar',  label: 'bar — pritisak' },
  { value: 'mgl',  label: 'mg/L — koncentracija' },
]

// ─── Reservoir SVG gauge ───────────────────────────────────────────────────────

function ReservoirGauge({ depth, maxDepth, critical }: {
  depth: number | null
  maxDepth: number
  critical: number | null
}) {
  const pct   = depth !== null ? Math.max(0, Math.min(1, depth / maxDepth)) : 0
  const w = 80, h = 100, pad = 8
  const waterH = (h - pad * 2) * pct
  const waterY = pad + (h - pad * 2) * (1 - pct)
  const critY  = critical !== null
    ? pad + (h - pad * 2) * (1 - Math.min(1, critical / maxDepth))
    : null

  const isAlarm = depth !== null && critical !== null && depth <= critical + 0.2
  const isWarn  = depth !== null && critical !== null && depth <= critical + 0.8 && !isAlarm
  const color   = depth === null ? '#94A3B8' : isAlarm ? ACCENT.orange : isWarn ? ACCENT.amber : ACCENT.active

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <rect x={pad} y={pad} width={w - pad * 2} height={h - pad * 2}
            fill="#F8FAFC" stroke="#E2E8F0" strokeWidth={1.5} rx={4} />
      {depth !== null && (
        <rect x={pad + 1} y={waterY} width={w - pad * 2 - 2} height={waterH}
              fill={color} opacity={0.75} rx={2} />
      )}
      {critY !== null && (
        <line x1={pad} y1={critY} x2={w - pad} y2={critY}
              stroke={ACCENT.orange} strokeWidth={1.5} strokeDasharray="3 2" />
      )}
      <text x={w / 2} y={h / 2 + 4} textAnchor="middle"
            fontSize={13} fontWeight="bold"
            fill={depth !== null ? '#1E293B' : '#94A3B8'}>
        {depth !== null ? `${depth.toFixed(2)}m` : '—'}
      </text>
    </svg>
  )
}

// ─── Pump / reservoir live cards ────────────────────────────────────────────────

function PumpBadge({ status, severity, active, lastSeenAt }: {
  status: PumpStatus['status']
  severity: PumpStatus['status_severity']
  active: boolean | null
  lastSeenAt: string | null
}) {
  const gray = 'bg-gray-100 text-gray-500 border border-gray-200'
  if (status === 'no_signal') return (
    <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${gray}`}>
      NEPOZNATO{lastSeenAt ? ` · ${fmtAgo(lastSeenAt)}` : ''}
    </span>
  )
  if (status === 'inactive') return (
    <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${gray}`}>NEAKTIVAN</span>
  )
  // The live stream flips `active` between /latest refetches; status only
  // says whether the device is reporting, so running/stopped follows it.
  const running = active ?? status === 'running'
  const alerting = severity === 'critical' || severity === 'warning'
  const tone = alerting
    ? (severity === 'critical' ? 'bg-red-50 text-brand-critical-text border border-red-200' : 'bg-orange-50 text-brand-warning-text border border-orange-200')
    : running ? 'bg-blue-50 text-brand-cyan-text border border-blue-200' : gray
  const dot = alerting
    ? (severity === 'critical' ? 'bg-brand-critical' : 'bg-brand-warning')
    : running ? 'bg-brand-cyan' : 'bg-gray-400'
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold ${tone}`}>
      <span className={`w-2 h-2 rounded-full ${dot}`} />
      {running ? 'RADI' : 'STOP'}
    </span>
  )
}

function PumpCard({ pump, history, onSimulate, onOpenDetail, muted = false }: {
  pump: PumpStatus
  history: PumpHistoryPoint[]
  onSimulate?: () => void
  onOpenDetail?: () => void
  muted?: boolean
}) {
  const sparkData = history.slice(-60).map((h) => ({ t: fmtTime(h.recorded_at), v: h.active ? 1 : 0 }))
  const isActive  = pump.active === true
  const isUnknown = pump.status === 'no_signal' || pump.active === null

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-xs p-5">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 truncate">{pump.label}</h3>
          <p className="text-xs text-gray-400 mt-1">Ažurirano: {fmtAgo(pump.recorded_at)}</p>
          {pump.fault === true && (
            <p className="mt-1"><FaultNotice since={pump.fault_since} critical={pump.status_severity === 'critical'} /></p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {muted && <Badge className="bg-slate-100 text-slate-600 border border-dashed border-slate-400">Alarmi utišani</Badge>}
          {pump.comm_error && (
            <Badge tone="critical" title={pump.comm_error_detail ?? undefined}>
              {commErrorLabel(pump.comm_error)}
            </Badge>
          )}
          <PumpBadge status={pump.status} severity={pump.status_severity} active={pump.active} lastSeenAt={pump.last_seen_at ?? pump.recorded_at} />
          {onSimulate && (
            <button onClick={onSimulate} title="Simuliraj alarm offline"
              className="px-2 py-0.5 rounded-md border border-gray-200 text-[10px] font-bold text-orange-600 hover:bg-orange-50">
              TEST
            </button>
          )}
          {onOpenDetail && (
            <button onClick={onOpenDetail} className="text-xs font-medium text-brand-cyan-text hover:text-brand-midnight">
              Detalji
            </button>
          )}
        </div>
      </div>

      {/* Reported live: only reachable via "Detalji" before this, same as a
          pump's flow/pressure were. Mirrors ReservoirCard's own inline-stat
          tiles below -- both null when not reporting/not wired, same as the
          reservoir's Dubina tile already handles. */}
      {(pump.flow_lps !== null || pump.pressure_kpa !== null) && (
        <div className="flex gap-2 mb-3">
          {pump.flow_lps !== null && <Stat label="Protok" value={`${pump.flow_lps.toFixed(2)} L/s`} />}
          {pump.pressure_kpa !== null && <Stat label="Pritisak" value={`${pump.pressure_kpa.toFixed(1)} kPa`} />}
        </div>
      )}

      {sparkData.length > 1 && (
        <div>
          <p className="text-xs text-gray-400 mb-1">Aktivnost — poslednjih {sparkData.length} očitavanja</p>
          <ResponsiveContainer width="100%" height={56}>
            <LineChart data={sparkData}>
              <Line type="step" dataKey="v" stroke={isActive ? ACCENT.active : '#CBD5E1'} strokeWidth={2} dot={false} />
              <XAxis dataKey="t" hide />
              <YAxis hide domain={[-0.1, 1.1]} />
              <ReferenceLine y={0.5} stroke="#E2E8F0" strokeDasharray="2 2" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {isUnknown && <p className="text-sm text-gray-400 text-center py-4">Nema podataka sa senzora</p>}
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg px-3 py-1.5 bg-gray-50 border border-gray-100">
      <p className="text-[11px] text-gray-400">{label}</p>
      <p className="text-sm font-semibold text-gray-900 mt-0.5">{value}</p>
    </div>
  )
}

function ReservoirCard({ res, history, onSimulate, onOpenDetail, muted = false }: {
  res: ReservoirStatus
  history: ReservoirHistoryPoint[]
  onSimulate?: () => void
  onOpenDetail?: () => void
  muted?: boolean
}) {
  // no_signal is the backend's call (staleness rule shared with the alert
  // watchdog) -- minutes_to_critical/rate_m_per_h are derived from depth
  // history that's suspect once the device has gone silent, so treat it like
  // the never-connected case throughout.
  const isStale = res.depth_m === null || res.status === 'no_signal'
  const depth               = isStale ? null : res.depth_m
  const minutesToCritical   = isStale ? null : res.minutes_to_critical
  const rateMPerH           = isStale ? null : res.rate_m_per_h

  const maxDepth  = Math.max(5, (depth ?? 0) * 1.3, (res.critical_depth_m ?? 0) * 4)
  const countdown = fmtCountdown(minutesToCritical)
  const draining  = rateMPerH !== null && rateMPerH < -0.001
  const filling   = rateMPerH !== null && rateMPerH > 0.001

  const isCritical = !isStale && res.status_severity === 'critical'
  const isWarning  = !isStale && res.status_severity === 'warning'

  const sparkData = history.slice(-72).map((h) => ({ t: fmtTime(h.recorded_at), v: h.depth_m }))

  return (
    <div className={`bg-white border rounded-xl shadow-xs p-5 ${
      isCritical ? 'border-red-300 ring-1 ring-red-100' : isWarning ? 'border-amber-300' : 'border-gray-200'
    }`}>
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 truncate">{res.label}</h3>
          <p className="text-xs text-gray-400 mt-1">Ažurirano: {fmtAgo(res.recorded_at)}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {muted && <Badge className="bg-slate-100 text-slate-600 border border-dashed border-slate-400">Alarmi utišani</Badge>}
          {res.comm_error && (
            <Badge tone="critical" title={res.comm_error_detail ?? undefined}>
              {commErrorLabel(res.comm_error)}
            </Badge>
          )}
          {onSimulate && (
            <button onClick={onSimulate} title="Simuliraj alarm niskog nivoa"
              className="px-2 py-0.5 rounded-md border border-gray-200 text-[10px] font-bold text-orange-600 hover:bg-orange-50">
              TEST
            </button>
          )}
          {onOpenDetail && (
            <button onClick={onOpenDetail} className="text-xs font-medium text-brand-cyan-text hover:text-brand-midnight">
              Detalji
            </button>
          )}
          {rateMPerH !== null && (
            <span className={`text-[11px] font-bold px-2 py-1 rounded-full border ${
              draining ? 'bg-red-50 text-red-600 border-red-200' : filling ? 'bg-blue-50 text-brand-cyan-text border-blue-200' : 'bg-gray-100 text-gray-500 border-gray-200'
            }`}>
              {draining ? '▼' : filling ? '▲' : '—'} {Math.abs(rateMPerH).toFixed(3)} m/h
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-5">
        <ReservoirGauge depth={depth} maxDepth={maxDepth} critical={res.critical_depth_m} />

        <div className="flex-1 flex flex-col gap-2">
          <Stat label="Dubina" value={depth !== null ? `${depth.toFixed(3)} m` : '—'} />
          <Stat label="Min. operativna dubina" value={res.critical_depth_m !== null ? `${res.critical_depth_m.toFixed(2)} m` : '—'} />

          {countdown && (
            <div className={`rounded-lg px-3 py-2 border ${
              isCritical ? 'bg-red-50 border-red-200' : isWarning ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-100'
            }`}>
              <p className="text-[11px] text-gray-400">Do gubitka pritiska</p>
              <p className={`text-sm font-bold mt-0.5 ${isCritical ? 'text-red-600' : isWarning ? 'text-amber-600' : 'text-gray-900'}`}>{countdown}</p>
            </div>
          )}

          {!isStale && !draining && !isCritical && (
            <div className="rounded-lg px-3 py-2 border bg-blue-50 border-blue-100">
              <p className="text-[11px] text-gray-400">Status</p>
              <p className="text-sm font-semibold text-brand-cyan-text mt-0.5">{filling ? 'Puni se' : 'Stabilan'}</p>
            </div>
          )}
        </div>
      </div>

      {sparkData.length > 1 && (
        <div className="mt-4">
          <p className="text-xs text-gray-400 mb-1">Dubina poslednjih {sparkData.length} očitavanja</p>
          <ResponsiveContainer width="100%" height={70}>
            <LineChart data={sparkData}>
              <Line type="monotone" dataKey="v" stroke={isCritical ? ACCENT.orange : ACCENT.active} strokeWidth={1.5} dot={false} />
              <XAxis dataKey="t" hide />
              <YAxis hide domain={[0, maxDepth]} />
              {res.critical_depth_m && <ReferenceLine y={res.critical_depth_m} stroke={ACCENT.orange} strokeDasharray="3 2" />}
              <Tooltip
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 11, color: '#1E293B' }}
                formatter={(v: number) => [`${v.toFixed(3)} m`, 'Dubina']}
                labelFormatter={(l) => l}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}

// A standalone device (flow meter, pressure sensor, level sensor, valve) had
// no card of its own on this dashboard at all -- reported live: an attached
// flow meter was findable nowhere near the reservoirs its pipe connects.
// Lighter-weight than PumpCard/ReservoirCard (no sparkline, no simulate --
// those are specific to pump/reservoir alerting) but the same shell/Detalji
// convention, so it reads as one family of card, not a bolted-on afterthought.
function GenericDeviceCard({ device, pipeLabel, onOpenDetail }: {
  device: ScadaDevice
  pipeLabel: string | null
  onOpenDetail?: () => void
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-xs p-5">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 truncate">{device.label}</h3>
          <p className="text-xs text-gray-400 mt-1">{NODE_TYPE_LABEL[device.node_type] ?? device.node_type} · Ažurirano: {fmtAgo(device.latest_recorded_at)}</p>
          {pipeLabel && <p className="text-xs text-gray-400 mt-0.5">Na cevovodu: {pipeLabel}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {device.severity && <Badge tone={severityTone(device.severity)}>{device.severity === 'critical' ? 'Kritično' : 'Upozorenje'}</Badge>}
          {onOpenDetail && (
            <button onClick={onOpenDetail} className="text-xs font-medium text-brand-cyan-text hover:text-brand-midnight">
              Detalji
            </button>
          )}
        </div>
      </div>
      {device.latest_value !== null ? (
        <p className="text-2xl font-bold text-gray-900">
          {device.latest_value.toFixed(2)}
          {device.measurement_unit && <span className="text-sm font-normal text-gray-400 ml-1">{device.measurement_unit}</span>}
        </p>
      ) : (
        <p className="text-sm text-gray-400">Nema podataka sa senzora</p>
      )}
    </div>
  )
}

// ─── Live event log + connection badge ─────────────────────────────────────────

interface LogEntry { id: string; text: string; time: string; type: 'pump' | 'reservoir' }

function EventLog({ entries }: { entries: LogEntry[] }) {
  return (
    <div className="bg-white rounded-xl shadow-xs border border-gray-100 p-6">
      <h3 className="text-base font-semibold text-gray-900 mb-4">Poslednji SCADA događaji</h3>
      {entries.length === 0 ? (
        <p className="text-sm text-gray-400">Čekanje na podatke sa Java SCADA sistema…</p>
      ) : (
        <ul className="flex flex-col gap-2 max-h-56 overflow-y-auto">
          {entries.map((e) => (
            <li key={e.id} className="flex items-center gap-3 text-sm">
              <span className="text-gray-400 tabular-nums min-w-[70px]">{e.time}</span>
              <span className={`w-2 h-2 rounded-full shrink-0 ${e.type === 'pump' ? 'bg-brand-cyan' : 'bg-slate-400'}`} />
              <span className="text-gray-700">{e.text}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// The old status bar held one full pill — dot + "UŽIVO"/"Prekid veze" —
// for the browser's own SSE connection. Repurposed per-node: same pill
// weight, but the label is the edge server's name and the dot/pulse now
// reads that node's own reachability (edgeConnStatus), not the browser
// tab's socket state. One pill per edge node, so the row grows/shrinks
// with however many a tenant has.
function EdgeServerBadge({ node }: { node: EdgeNodeSummary }) {
  const status = edgeConnStatus(node)
  return (
    <span className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold border ${EDGE_STATUS_PILL[status]}`}>
      <span className={`w-2 h-2 rounded-full ${EDGE_STATUS_DOT[status]} ${status === 'ok' ? 'animate-pulse' : ''}`} />
      {node.name}
    </span>
  )
}

// ─── Topology view — zone-resolution semantic zoom ─────────────────────────────

// Zone fill/marker color follows the backend's zone severity (deficit =
// critical, surplus = warning, balanced = ok, unknown = unknown) -- the same
// severity the zone push carries -- not the flow status directly, so surplus
// no longer reads as "fine".
const ZONE_SEVERITY_COLOR: Record<StatusSeverity, string> = {
  critical: 'var(--color-brand-critical)',
  warning:  'var(--color-brand-warning)',
  ok:       'var(--color-brand-cyan)',
  unknown:  'var(--color-brand-gray)',
}

// The fill colors are right for large map surfaces but fail WCAG as small
// popup text (docs/branding_guide.md §3c), hence the -text variants.
const ZONE_SEVERITY_TEXT_COLOR: Record<StatusSeverity, string> = {
  critical: 'var(--color-brand-critical-text)',
  warning:  'var(--color-brand-warning-text)',
  ok:       'var(--color-brand-cyan-text)',
  unknown:  'var(--color-brand-gray-text)',
}

const ZONE_STATUS_LABEL: Record<ZoneFlowStatus, string> = {
  deficit:  'Deficit',
  surplus:  'Suficit',
  balanced: 'Balansirano',
  unknown:  'Nepoznato',
}

// Popup wording for a well/reservoir zone's reservoir-derived Signal 1 —
// "Deficit"/"Suficit"/"Balansirano" above describe flow balance and don't
// apply once the color is describing the reservoir's own health instead.
const CATEGORY_ZONE_LABEL: Record<PulseCategory, string> = {
  positive:     'Dobro',
  problem:      'Problem',
  disconnected: 'Nepoznato',
}

// muted keeps the real severity color (an alarm-worthy reading on a muted
// device is still worth seeing) but swaps the border to dashed gray — a
// shape/pattern cue, not just another color, so mute state doesn't ride on
// color perception alone and can't be confused with the device's actual
// status color.
function circleIcon(color: string, size: number, muted = false) {
  const border = muted ? `${size > 16 ? 3 : 2}px dashed #62697F` : `${size > 16 ? 3 : 2}px solid white`
  return L.divIcon({
    className: '',
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:${border};box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  })
}

// A separate function from circleIcon's own `muted` (alert-muted, still a
// real editable point at full opacity) -- these are DockedMapEditor's
// non-interactive reference markers, existing infrastructure shown for
// spatial context while editing something else nearby (so a well's dot
// doesn't disappear while its zone's boundary is drawn around it). Lower
// opacity is what marks "background," not the dashed border alone, so the
// two concepts can't be confused for each other on the same map.
function mutedReferenceIcon(color: string, size = 10) {
  return L.divIcon({
    className: '',
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2px dashed #62697F;opacity:0.55"></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  })
}

function zoneCentroid(zoneId: string, devices: ScadaDevice[]): [number, number] | null {
  const located = devices.filter((d) => d.zone_id === zoneId && d.location_lat != null && d.location_lng != null)
  if (located.length === 0) return null
  return [
    located.reduce((s, d) => s + d.location_lat!, 0) / located.length,
    located.reduce((s, d) => s + d.location_lng!, 0) / located.length,
  ]
}

// A zone's "connecting object" for pipeline anchoring, per the decision in
// overwatch-to-web-2026-09-21-zone-anchor-pipeline-default-line.md: the
// specific defining device's own coordinate, not an average of everything
// located in the zone (zoneCentroid) — a reservoir plus an adjacent valve
// would otherwise pull the anchor away from the reservoir's true spot.
// Only well/reservoir zones have one canonical device type; distribution/
// source zones have no single "object," so they fall back to zoneCentroid,
// same as every other zone-without-a-drawn-boundary case already does.
const ZONE_ANCHOR_NODE_TYPE: Partial<Record<ZoneCategory, NodeType>> = {
  well:      'pump',
  reservoir: 'reservoir',
}

// Same anchor, plus the resolved device when there is one -- the pipeline
// editor's own point-seeding (below) needs to know not just where a zone's
// anchor sits but whether it IS a specific device, so a seeded endpoint can
// be a real device-attached point rather than an untyped bend at that spot.
function zoneAnchorObject(zoneId: string | null, zones: ScadaZone[], devices: ScadaDevice[]): { lat: number; lng: number; device: ScadaDevice | null } | null {
  if (!zoneId) return null
  const zone = zones.find((z) => z.id === zoneId)
  const nodeType = zone ? ZONE_ANCHOR_NODE_TYPE[zone.zone_category] : undefined
  if (nodeType) {
    // First match wins if a zone somehow holds more than one — same
    // one-canonical-device assumption the Signal-1 fallback above makes.
    const device = devices.find((d) => d.zone_id === zoneId && d.node_type === nodeType && d.location_lat != null && d.location_lng != null)
    if (device) return { lat: device.location_lat!, lng: device.location_lng!, device }
  }
  const centroid = zoneCentroid(zoneId, devices)
  return centroid ? { lat: centroid[0], lng: centroid[1], device: null } : null
}

function zoneAnchor(zoneId: string | null, zones: ScadaZone[], devices: ScadaDevice[]): [number, number] | null {
  const a = zoneAnchorObject(zoneId, zones, devices)
  return a ? [a.lat, a.lng] : null
}

// Monotone chain convex hull over [lat, lng] points (lng as x, lat as y).
function convexHull(points: [number, number][]): [number, number][] {
  const pts = [...points].sort((a, b) => a[1] - b[1] || a[0] - b[0])
  const cross = (o: [number, number], a: [number, number], b: [number, number]) =>
    (a[1] - o[1]) * (b[0] - o[0]) - (a[0] - o[0]) * (b[1] - o[1])

  const lower: [number, number][] = []
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop()
    lower.push(p)
  }
  const upper: [number, number][] = []
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i]
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop()
    upper.push(p)
  }
  lower.pop(); upper.pop()
  return lower.concat(upper)
}

// Area overlay for a zone's footprint. Needs 3+ located devices to form a
// polygon — zones below that just keep the centroid dot (see zoneCentroid).
function zoneHull(zoneId: string, devices: ScadaDevice[]): [number, number][] | null {
  const located = devices.filter((d) => d.zone_id === zoneId && d.location_lat != null && d.location_lng != null)
  if (located.length < 3) return null
  const hull = convexHull(located.map((d): [number, number] => [d.location_lat!, d.location_lng!]))
  return hull.length >= 3 ? hull : null
}

// ─── Pipeline path (zone-link) editing ─────────────────────────────────────────

function haversineMeters(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371000
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const s = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(s))
}

// A path point with guaranteed coordinates -- the shape the pipeline path
// editor's local state actually keeps (see PipelinePathEditorModal's
// `points` state below): ScadaZoneLinkPoint.lat/lng are nullable on the wire
// (device-ownership step 2, a device/access_point point with no location
// yet), but the editor normalizes every point to a real coordinate the
// moment it loads, so nothing downstream -- order math, DockedMapEditor's
// LatLng bound, map rendering -- has to think about null again.
type LocatedPathPoint = Omit<ScadaZoneLinkPoint, 'lat' | 'lng'> & { lat: number; lng: number }

// Suggests an `order` for a newly-dropped point, per docs/plan-scada-gps-capture.md:
// nearest segment by perpendicular distance, then proportional interpolation by
// real-world distance along that segment. Falls back to append/prepend at the ends.
function suggestPointOrder(sorted: LocatedPathPoint[], click: { lat: number; lng: number }): number {
  if (sorted.length === 0) return 10
  if (sorted.length === 1) return sorted[0].order + 10

  let best = { index: 0, distSq: Infinity, t: 0 }
  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i]
    const b = sorted[i + 1]
    const abx = b.lng - a.lng, aby = b.lat - a.lat
    const apx = click.lng - a.lng, apy = click.lat - a.lat
    const abLenSq = abx * abx + aby * aby
    const rawT = abLenSq === 0 ? 0 : (apx * abx + apy * aby) / abLenSq
    const t = Math.max(0, Math.min(1, rawT))
    const px = a.lng + t * abx, py = a.lat + t * aby
    const distSq = (click.lng - px) ** 2 + (click.lat - py) ** 2
    if (distSq < best.distSq) best = { index: i, distSq, t: rawT }
  }

  if (best.index === 0 && best.t < 0) return sorted[0].order - 10
  if (best.index === sorted.length - 2 && best.t > 1) return sorted[sorted.length - 1].order + 10

  const a = sorted[best.index]
  const b = sorted[best.index + 1]
  const segLen = haversineMeters(a, b)
  const ratio = segLen === 0 ? 0.5 : Math.max(0.02, Math.min(0.98, haversineMeters(a, click) / segLen))
  return a.order + ratio * (b.order - a.order)
}

// Default position for a "quick attach" (CevovodiGrid's folded-row dropdowns
// and expanded-panel search-and-add) -- these attach a device/access point
// as a real path point without opening the map editor, so they need
// somewhere reasonable to put it. Two cases:
//  - the pipe already has a real drawn path (2+ located points): the point
//    at 50% of the path's total length, walking segment by segment -- same
//    interpolation suggestPointOrder already does per-segment, just summed
//    along the whole line instead of searching for the nearest one to a
//    click. `order` is interpolated the same way, so the new point actually
//    lands in the middle of the sequence, not just visually near the middle.
//  - fewer than 2 located points (a brand-new pipe, or one that's only ever
//    had its anchor-seeded points): the average of the two zone anchors
//    (zoneAnchor) -- the same anchors PipelinePathEditorModal seeds as real
//    points when it opens, so this stays consistent with that.
// Returns null when neither is resolvable (no path, and no zone anchor on
// either end) -- callers must disable rather than guess in that case.
function pipeMidpoint(link: ScadaZoneLink, zones: ScadaZone[], devices: ScadaDevice[]): { lat: number; lng: number; order: number } | null {
  const located = [...link.path].sort((a, b) => a.order - b.order)
    .filter((p): p is ScadaZoneLinkPoint & { lat: number; lng: number } => p.lat != null && p.lng != null)

  if (located.length >= 2) {
    const segLens = located.slice(0, -1).map((p, i) => haversineMeters(p, located[i + 1]))
    const total = segLens.reduce((s, d) => s + d, 0)
    if (total > 0) {
      const target = total / 2
      let acc = 0
      for (let i = 0; i < segLens.length; i++) {
        if (acc + segLens[i] >= target || i === segLens.length - 1) {
          const a = located[i], b = located[i + 1]
          const t = segLens[i] === 0 ? 0.5 : Math.max(0, Math.min(1, (target - acc) / segLens[i]))
          return {
            lat: a.lat + t * (b.lat - a.lat),
            lng: a.lng + t * (b.lng - a.lng),
            order: a.order + t * (b.order - a.order),
          }
        }
        acc += segLens[i]
      }
    }
  }

  const from = zoneAnchor(link.from_zone_id, zones, devices)
  const to   = zoneAnchor(link.to_zone_id, zones, devices)
  if (from && to) {
    const maxOrder = link.path.reduce((m, p) => Math.max(m, p.order), 0)
    return { lat: (from[0] + to[0]) / 2, lng: (from[1] + to[1]) / 2, order: maxOrder + 10 }
  }
  return null
}

// Same nearest-edge logic as suggestPointOrder, but for a closed ring: every
// vertex connects to the next, including the wraparound edge from the last
// vertex back to the first. Unlike an open polyline, a click is never "off
// the end" — with 3+ vertices every edge (wraparound included) is a valid
// interior segment. A point on the wraparound edge always becomes the new
// last point (its order can't be interpolated between "highest" and
// "lowest" the way an interior edge's can).
function suggestPolygonPointOrder(sorted: ScadaBoundaryPoint[], click: { lat: number; lng: number }): number {
  if (sorted.length === 0) return 10
  if (sorted.length < 3) return sorted[sorted.length - 1].order + 10

  let best = { index: 0, distSq: Infinity }
  for (let i = 0; i < sorted.length; i++) {
    const a = sorted[i]
    const b = sorted[(i + 1) % sorted.length]
    const abx = b.lng - a.lng, aby = b.lat - a.lat
    const apx = click.lng - a.lng, apy = click.lat - a.lat
    const abLenSq = abx * abx + aby * aby
    const rawT = abLenSq === 0 ? 0 : (apx * abx + apy * aby) / abLenSq
    const t = Math.max(0, Math.min(1, rawT))
    const px = a.lng + t * abx, py = a.lat + t * aby
    const distSq = (click.lng - px) ** 2 + (click.lat - py) ** 2
    if (distSq < best.distSq) best = { index: i, distSq }
  }

  const a = sorted[best.index]
  if (best.index === sorted.length - 1) return a.order + 10 // wraparound edge

  const b = sorted[best.index + 1]
  const segLen = haversineMeters(a, b)
  const ratio = segLen === 0 ? 0.5 : Math.max(0.02, Math.min(0.98, haversineMeters(a, click) / segLen))
  return a.order + ratio * (b.order - a.order)
}

function pointIcon(color: string) {
  return L.divIcon({
    className: '',
    html: `<div style="width:10px;height:10px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,.5);cursor:move"></div>`,
    iconSize: [10, 10],
    iconAnchor: [5, 5],
  })
}

// Diamond, not circle — distinct from both a plain bend's pointIcon() and a
// device's circleIcon(). POI blue (guide §4A: location markers whose color
// signals place, not state), not one of the status/accent colors.
function accessPointIcon() {
  return L.divIcon({
    className: '',
    html: `<div style="width:11px;height:11px;background:#019FD9;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,.5);transform:rotate(45deg);cursor:move"></div>`,
    iconSize: [11, 11],
    iconAnchor: [5, 5],
  })
}

const POINT_TYPE_LABEL: Record<PathPointType, string> = {
  bend:          'Obična tačka',
  device:        'Uređaj',
  access_point:  'Pristupna tačka',
}
const POINT_TYPE_OPTIONS = (Object.entries(POINT_TYPE_LABEL) as [PathPointType, string][])
  .map(([value, label]) => ({ value, label }))

function deviceColor(d: ScadaDevice, pumpByKey: Map<string, PumpStatus>, reservoirByKey: Map<string, ReservoirStatus>): string {
  return pulseColor(devicePulse(d, pumpByKey, reservoirByKey))
}

// ─── Pipeline path editor — dedicated modal, point list + map side by side ─────

function PipelinePathEditorModal({ link, onClose }: { link: ScadaZoneLink; onClose: () => void }) {
  const qc = useQueryClient()
  // A device/access_point point's lat/lng come back null when the
  // referenced object has no location yet (device-ownership step 2: one
  // location per device, a path point references it rather than storing
  // its own). Seeded here to the centroid of this pipe's other located
  // points (or FALLBACK_CENTER if none) so the rest of this component
  // never has to handle a null coordinate -- the point just renders there
  // until someone drags it into place, at which point the backend adopts
  // that position as the device's own (devicesNeedingLocationUpdate below
  // also pushes it explicitly, for the already-located case).
  const [points, setPoints] = useState<LocatedPathPoint[]>(() => {
    const sorted = [...link.path].sort((a, b) => a.order - b.order)
    const located = sorted.filter((p): p is ScadaZoneLinkPoint & { lat: number; lng: number } => p.lat != null && p.lng != null)
    const fallback: [number, number] = located.length > 0
      ? [located.reduce((s, p) => s + p.lat, 0) / located.length, located.reduce((s, p) => s + p.lng, 0) / located.length]
      : FALLBACK_CENTER
    return sorted.map((p) => ({ ...p, lat: p.lat ?? fallback[0], lng: p.lng ?? fallback[1] }))
  })
  const [error, setError]   = useState('')
  const editorRef = useRef<DockedMapEditorHandle>(null)

  const { data: devices = [] }      = useQuery({ queryKey: ['scada-devices'],       queryFn: () => scadaApi.listDevices().then((r) => r.data) })
  const { data: accessPoints = [] } = useQuery({ queryKey: ['scada-access-points'], queryFn: () => scadaApi.listAccessPoints().then((r) => r.data) })
  const { data: zones = [] }        = useQuery({ queryKey: ['scada-zones'],         queryFn: () => scadaApi.listZones().then((r) => r.data), staleTime: 60_000 })
  const { data: latest }            = useQuery({ queryKey: ['scada-latest'],        queryFn: () => scadaApi.latest().then((r) => r.data), refetchInterval: 30_000 })
  const pumpByKey      = new Map((latest?.pumps ?? []).map((p) => [p.device_key, p]))
  const reservoirByKey = new Map((latest?.reservoirs ?? []).map((r) => [r.device_key, r]))

  // Attaching a device/access point here is a reference, not a move
  // (docs/decision-device-ownership-and-attachment.md, binding): its owner
  // zone is untouched. The old "detach before placing on a pipe" 409 is
  // gone server-side (device-ownership step 1) -- picking an already-zoned
  // device just attaches it, nothing to clear.
  const deviceById       = new Map(devices.map((d) => [d.id, d]))
  const accessPointById  = new Map(accessPoints.map((ap) => [ap.id, ap]))
  const zoneLabel        = (zoneId: string | null) => zones.find((z) => z.id === zoneId)?.label ?? null
  // zone_id is now always set (owner, required) and access_point_id is an
  // independent, optional attachment -- a device can be both at once (e.g.
  // owned by "Bunar B1" and sitting inside the chlorination access point),
  // so both show together rather than one hiding the other.
  const deviceOptionLabel = (d: ScadaDevice) =>
    `${d.label} (${d.device_key}) — zona: ${zoneLabel(d.zone_id) ?? '?'}${d.access_point_id ? ' · pristupna tačka' : ''}`
  const deviceSelectOptions = [
    { value: '', label: '— Izaberite uređaj —' },
    ...devices.map((d) => ({ value: d.id, label: deviceOptionLabel(d) })),
  ]
  const accessPointSelectOptions = [
    { value: '', label: '— Izaberite pristupnu tačku —' },
    ...accessPoints.filter((ap) => ap.active).map((ap) => ({ value: ap.id, label: ap.label })),
  ]

  const invalidPoints = points.some((p) =>
    (p.point_type === 'device' && !p.device_id) || (p.point_type === 'access_point' && !p.access_point_id))

  // A point attached to a device/access point is the *same physical spot* as
  // that object, not an independent coordinate that happens to line up —
  // the incident this whole attachment feature was built for was exactly a
  // pipe endpoint silently drifting from where the sensor it represented
  // actually was. So the link is bidirectional: picking an object snaps the
  // point to its stored location (below, and on load via the effect further
  // down); dragging the point afterward is *editing that object's location*,
  // pushed back on Sačuvaj (devicesNeedingLocationUpdate/accessPoints...
  // below). Applies uniformly to every point, not just endpoints — endpoints
  // just happen to be where this matters most in practice, since they're
  // where a pipe actually touches a well/reservoir's equipment.
  const resyncedOnLoadRef = useRef(false)
  useEffect(() => {
    if (resyncedOnLoadRef.current) return
    if (devices.length === 0 && accessPoints.length === 0) return
    resyncedOnLoadRef.current = true
    setPoints((pts) => pts.map((p) => {
      if (p.point_type === 'device' && p.device_id) {
        const d = devices.find((x) => x.id === p.device_id)
        if (d?.location_lat != null && d?.location_lng != null) return { ...p, lat: d.location_lat, lng: d.location_lng }
      }
      if (p.point_type === 'access_point' && p.access_point_id) {
        const ap = accessPoints.find((x) => x.id === p.access_point_id)
        if (ap?.location_lat != null && ap?.location_lng != null) return { ...p, lat: ap.location_lat, lng: ap.location_lng }
      }
      return p
    }))
  }, [devices, accessPoints])

  // A pipe whose from/to zones are already set in the Cevovodi table should
  // open here with real, editable points at those zones' own anchors, not
  // start from whatever near-empty path happens to be saved -- the same
  // anchors the topology map's read-only default line already uses
  // (zoneAnchorObject), made real the moment someone opens the editor to
  // actually draw the route, instead of staying a synthetic preview.
  // path.length < 2 mirrors that same "not really a drawn path yet"
  // threshold; a single stray point at that stage isn't a route either, so
  // it's replaced rather than kept alongside a second, possibly-redundant
  // anchor. When the anchor resolves to a specific device (well/reservoir
  // zones), the seeded point references it directly rather than sitting as
  // an untyped bend at the same coordinate.
  const anchorsSeededRef = useRef(false)
  useEffect(() => {
    if (anchorsSeededRef.current) return
    if (zones.length === 0 || devices.length === 0) return
    anchorsSeededRef.current = true
    setPoints((pts) => {
      if (pts.length >= 2) return pts
      const from = zoneAnchorObject(link.from_zone_id, zones, devices)
      const to   = zoneAnchorObject(link.to_zone_id, zones, devices)
      if (!from || !to) return pts
      const toPoint = (a: NonNullable<typeof from>, order: number): LocatedPathPoint => ({
        lat: a.lat, lng: a.lng, recorded_at: new Date().toISOString(), order,
        point_type: a.device ? 'device' : 'bend',
        device_id: a.device?.id ?? null,
        access_point_id: null,
      })
      return [toPoint(from, 10), toPoint(to, 20)]
    })
  }, [zones, devices, link.from_zone_id, link.to_zone_id])

  // Devices/access points whose own location_lat/lng disagrees with where
  // their attached point now sits — pushed back to the object on save so
  // dragging a linked point really does move the object, not just this
  // pipe's private copy of the coordinate. Covers the reverse case too: an
  // object with no location yet adopts the point's, since null !== number.
  const devicesNeedingLocationUpdate = (() => {
    const seen = new Set<string>()
    const result: { device: ScadaDevice; lat: number; lng: number }[] = []
    for (const p of points) {
      if (p.point_type !== 'device' || !p.device_id || seen.has(p.device_id)) continue
      seen.add(p.device_id)
      const d = deviceById.get(p.device_id)
      if (d && (d.location_lat !== p.lat || d.location_lng !== p.lng)) result.push({ device: d, lat: p.lat, lng: p.lng })
    }
    return result
  })()
  const accessPointsNeedingLocationUpdate = (() => {
    const seen = new Set<string>()
    const result: { ap: ScadaAccessPoint; lat: number; lng: number }[] = []
    for (const p of points) {
      if (p.point_type !== 'access_point' || !p.access_point_id || seen.has(p.access_point_id)) continue
      seen.add(p.access_point_id)
      const ap = accessPointById.get(p.access_point_id)
      if (ap && (ap.location_lat !== p.lat || ap.location_lng !== p.lng)) result.push({ ap, lat: p.lat, lng: p.lng })
    }
    return result
  })()
  const savePathMut = useMutation({
    mutationFn: async () => {
      const deviceWrites = new Map<string, Partial<ScadaDevice>>()
      for (const { device, lat, lng } of devicesNeedingLocationUpdate) {
        deviceWrites.set(device.device_key, { location_lat: lat, location_lng: lng })
      }
      await Promise.all([
        ...[...deviceWrites].map(([deviceKey, body]) => scadaApi.updateDevice(deviceKey, body)),
        ...accessPointsNeedingLocationUpdate.map(({ ap, lat, lng }) =>
          scadaApi.updateAccessPoint(ap.id, { location_lat: lat, location_lng: lng })),
      ])
      if (deviceWrites.size > 0) qc.invalidateQueries({ queryKey: ['scada-devices'] })
      if (accessPointsNeedingLocationUpdate.length > 0) qc.invalidateQueries({ queryKey: ['scada-access-points'] })
      return scadaApi.updateZoneLinkPath(link.id, points)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scada-zone-links'] })
      qc.invalidateQueries({ queryKey: ['scada-devices'] })
      onClose()
    },
    onError: (e: unknown) => setError(apiError(e, 'Greška pri čuvanju putanje.')),
  })

  // Returns the new point's resulting index (post-sort) so DockedMapEditor
  // can auto-select it — the setPoints updater runs synchronously here, so
  // newIndex is correct by the time this function returns.
  const addPoint = (lat: number, lng: number): number => {
    let newIndex = -1
    setPoints((pts) => {
      const order = suggestPointOrder(pts, { lat, lng })
      const newPoint: LocatedPathPoint = { lat, lng, recorded_at: new Date().toISOString(), order, point_type: 'bend', device_id: null, access_point_id: null }
      const sorted = [...pts, newPoint].sort((a, b) => a.order - b.order)
      newIndex = sorted.indexOf(newPoint)
      return sorted
    })
    return newIndex
  }
  // isPlacement (only true for a just-added point's first commit -- see
  // DockedMapEditor's onCommitPoint doc) recomputes order from the point's
  // actual landing spot, excluding itself from the suggestion so it isn't
  // compared against its own still-stale position. A plain drag of an
  // already-placed point (isPlacement false) only ever updates lat/lng, so a
  // small nudge can't reshuffle the path.
  const movePoint = (index: number, lat: number, lng: number, isPlacement?: boolean): number | void => {
    if (!isPlacement) {
      setPoints((pts) => pts.map((p, i) => i === index ? { ...p, lat, lng } : p))
      return
    }
    let newIndex = index
    setPoints((pts) => {
      const others = pts.filter((_, i) => i !== index)
      const order = suggestPointOrder(others, { lat, lng })
      const updated = { ...pts[index], lat, lng, order }
      const sorted = [...others, updated].sort((a, b) => a.order - b.order)
      newIndex = sorted.indexOf(updated)
      return sorted
    })
    return newIndex
  }
  const removePoint = (index: number) => setPoints((pts) => pts.filter((_, i) => i !== index))
  const setPointType = (index: number, point_type: PathPointType) => {
    setPoints((pts) => pts.map((p, i) => i === index ? { ...p, point_type, device_id: null, access_point_id: null } : p))
  }
  const setPointDeviceId = (index: number, device_id: string | null) => {
    const loc = device_id ? deviceById.get(device_id) : null
    setPoints((pts) => pts.map((p, i) => {
      if (i !== index) return p
      if (loc?.location_lat != null && loc?.location_lng != null) return { ...p, device_id, lat: loc.location_lat, lng: loc.location_lng }
      return { ...p, device_id }
    }))
  }
  const setPointAccessPointId = (index: number, access_point_id: string | null) => {
    const loc = access_point_id ? accessPointById.get(access_point_id) : null
    setPoints((pts) => pts.map((p, i) => {
      if (i !== index) return p
      if (loc?.location_lat != null && loc?.location_lng != null) return { ...p, access_point_id, lat: loc.location_lat, lng: loc.location_lng }
      return { ...p, access_point_id }
    }))
  }

  const mapCenter: [number, number] = points.length > 0 ? [points[0].lat, points[0].lng] : FALLBACK_CENTER

  const markerIcon = (p: ScadaZoneLinkPoint) => {
    if (p.point_type === 'device' && p.device_id) {
      const d = deviceById.get(p.device_id)
      return d ? circleIcon(deviceColor(d, pumpByKey, reservoirByKey), 14, d.alerts_muted) : pointIcon(ACCENT.orange)
    }
    if (p.point_type === 'access_point') return accessPointIcon()
    return pointIcon(ACCENT.orange)
  }
  const markerLabel = (p: ScadaZoneLinkPoint, i: number) => {
    if (p.point_type === 'device' && p.device_id) return deviceById.get(p.device_id)?.label ?? `Tačka ${i + 1}`
    if (p.point_type === 'access_point' && p.access_point_id) return accessPointById.get(p.access_point_id)?.label ?? `Tačka ${i + 1}`
    return `Tačka ${i + 1}`
  }

  // Existing devices not already on this pipe, shown muted for spatial
  // context while placing/moving this pipe's own points -- reported live:
  // it's hard to place a point correctly when everything else nearby is
  // invisible. Severity-tinted (not flat) since pumpByKey/reservoirByKey
  // are already fetched here for the pipe's own device markers anyway.
  const ownDeviceIds = new Set(points.filter((p) => p.point_type === 'device' && p.device_id).map((p) => p.device_id))
  const referenceMarkers = devices
    .filter((d) => d.active && d.location_lat != null && d.location_lng != null && !ownDeviceIds.has(d.id))
    .map((d) => ({
      key: d.id, lat: d.location_lat!, lng: d.location_lng!,
      icon: mutedReferenceIcon(deviceColor(d, pumpByKey, reservoirByKey)),
      label: d.label,
    }))

  // inset-2/4, not a small centered box on a full dark backdrop: matches
  // the dashboard's own fullscreen-map sizing convention rather than
  // introducing a second, different "big overlay" shape. This is invoked
  // from two different parent contexts (TopologyView's map, and
  // CevovodiGrid's plain settings table below) with no shared positioned
  // ancestor, so `fixed` (viewport-relative) is used deliberately instead
  // of `absolute` docking within a specific parent -- when opened from
  // inside the dashboard's already-fullscreen map modal, this still reads
  // as "the same big surface's content changed" rather than a small popup
  // stacked on a bigger one, even though two DOM layers still exist.
  return (
    <div className="fixed inset-2 sm:inset-4 z-50 bg-white rounded-overlay shadow-overlay flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 shrink-0">
        <div>
          <h3 className="font-semibold text-gray-900">{link.label}</h3>
          <p className="text-xs text-gray-400">{points.length} tačaka</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
      </div>

      <div className="flex-1 min-h-0">
        <DockedMapEditor
          ref={editorRef}
          points={points}
          mapCenter={mapCenter}
          markerIcon={markerIcon}
          panelTitle={markerLabel}
          panelExtraFields={(p, i) => (
            <div className="space-y-1.5">
              <SelectCell value={p.point_type} onCommit={(v) => setPointType(i, v as PathPointType)}
                options={POINT_TYPE_OPTIONS} className={`${FORM_INPUT} text-xs`} />
              {p.point_type === 'device' && (
                <SelectCell value={p.device_id ?? ''} onCommit={(v) => setPointDeviceId(i, v || null)}
                  options={deviceSelectOptions} className={`${FORM_INPUT} text-xs`} />
              )}
              {p.point_type === 'access_point' && (
                <SelectCell value={p.access_point_id ?? ''} onCommit={(v) => setPointAccessPointId(i, v || null)}
                  options={accessPointSelectOptions} className={`${FORM_INPUT} text-xs`} />
              )}
            </div>
          )}
          onCommitPoint={movePoint}
          onAddPoint={addPoint}
          onDeletePoint={removePoint}
          renderOverlay={(pts) => pts.length >= 2 && (
            <Polyline positions={pts.map((p) => [p.lat, p.lng])} pathOptions={{ color: ACCENT.orange, weight: 3, dashArray: '6 6' }} />
          )}
          referenceMarkers={referenceMarkers}
        />
      </div>

      <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-gray-100 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => editorRef.current?.triggerAdd()}
            className="text-xs font-medium bg-white border border-gray-300 rounded-lg px-2.5 py-1.5 shadow-xs text-brand-cyan-text hover:bg-blue-50 shrink-0">
            + Dodaj tačku
          </button>
          {error ? <p className="text-xs text-red-600 truncate">{error}</p>
            : invalidPoints ? <p className="text-xs text-amber-600 truncate">Svaka tačka tipa "Uređaj"/"Pristupna tačka" mora imati izabran uređaj/pristupnu tačku.</p>
            : (devicesNeedingLocationUpdate.length > 0 || accessPointsNeedingLocationUpdate.length > 0)
            ? <p className="text-xs text-brand-cyan-text truncate">
                Ažurira se i lokacija: {[...devicesNeedingLocationUpdate.map((d) => d.device.label), ...accessPointsNeedingLocationUpdate.map((a) => a.ap.label)].join(', ')}
              </p>
            : <span className="text-xs text-gray-400 truncate">Klik na tačku je otvara za izmenu, prevlačenje je pomera.</span>}
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={onClose} className="text-sm font-medium text-gray-500 hover:text-gray-700 px-4 py-2 rounded-lg border border-gray-300">
            Otkaži
          </button>
          <button onClick={() => savePathMut.mutate()}
            disabled={savePathMut.isPending || invalidPoints}
            className="text-sm font-medium text-brand-midnight bg-brand-cyan hover:bg-brand-cyan-dark disabled:opacity-40 px-4 py-2 rounded-lg">
            Sačuvaj
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Zone boundary editor — closed-ring polygon, forked from the pipeline path
// editor above rather than parametrized into it: a boundary is a closed ring
// (suggestPolygonPointOrder's wraparound edge) with no recorded_at per point
// (a drawn boundary vertex is never field-GPS-captured, unlike a path point) ──

function ZoneBoundaryEditorModal({ zone, initialCenter, devices, onClose }: { zone: ScadaZone; initialCenter: [number, number] | null; devices: ScadaDevice[]; onClose: () => void }) {
  const qc = useQueryClient()
  const [points, setPoints] = useState<ScadaBoundaryPoint[]>(() => [...zone.boundary_polygon].sort((a, b) => a.order - b.order))
  const [error, setError]   = useState('')
  const editorRef = useRef<DockedMapEditorHandle>(null)

  // Flat muted gray, not severity-tinted -- unlike PipelinePathEditorModal
  // this modal doesn't otherwise need live pump/reservoir status, and these
  // dots are spatial reference only (so a well doesn't disappear while its
  // own zone's boundary is drawn around it), not something to monitor here.
  const referenceMarkers = devices
    .filter((d) => d.active && d.location_lat != null && d.location_lng != null)
    .map((d) => ({ key: d.id, lat: d.location_lat!, lng: d.location_lng!, icon: mutedReferenceIcon('#62697F'), label: d.label }))

  const canSave = points.length === 0 || points.length >= 3

  const saveBoundaryMut = useMutation({
    mutationFn: () => scadaApi.updateZoneBoundary(zone.id, points),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['scada-zones'] }); onClose() },
    onError: (e: unknown) => setError(apiError(e, 'Greška pri čuvanju granice.')),
  })

  const addPoint = (lat: number, lng: number): number => {
    let newIndex = -1
    setPoints((pts) => {
      const order = suggestPolygonPointOrder(pts, { lat, lng })
      const newPoint: ScadaBoundaryPoint = { lat, lng, order }
      const sorted = [...pts, newPoint].sort((a, b) => a.order - b.order)
      newIndex = sorted.indexOf(newPoint)
      return sorted
    })
    return newIndex
  }
  const movePoint = (index: number, lat: number, lng: number, isPlacement?: boolean): number | void => {
    if (!isPlacement) {
      setPoints((pts) => pts.map((p, i) => i === index ? { ...p, lat, lng } : p))
      return
    }
    let newIndex = index
    setPoints((pts) => {
      const others = pts.filter((_, i) => i !== index)
      const order = suggestPolygonPointOrder(others, { lat, lng })
      const updated = { ...pts[index], lat, lng, order }
      const sorted = [...others, updated].sort((a, b) => a.order - b.order)
      newIndex = sorted.indexOf(updated)
      return sorted
    })
    return newIndex
  }
  const removePoint = (index: number) => setPoints((pts) => pts.filter((_, i) => i !== index))

  const mapCenter: [number, number] = points.length > 0 ? [points[0].lat, points[0].lng] : initialCenter ?? FALLBACK_CENTER

  return (
    <div className="fixed inset-2 sm:inset-4 z-50 bg-white rounded-overlay shadow-overlay flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 shrink-0">
        <div>
          <h3 className="font-semibold text-gray-900">{zone.label} — granica</h3>
          <p className="text-xs text-gray-400">{points.length} tačaka</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
      </div>

      <div className="flex-1 min-h-0">
        <DockedMapEditor
          ref={editorRef}
          points={points}
          mapCenter={mapCenter}
          markerIcon={() => pointIcon(ACCENT.orange)}
          panelTitle={(_p, i) => `Tačka ${i + 1}`}
          onCommitPoint={movePoint}
          onAddPoint={addPoint}
          onDeletePoint={removePoint}
          renderOverlay={(pts) => pts.length >= 3 && (
            <Polygon positions={pts.map((p) => [p.lat, p.lng])} pathOptions={{ color: ACCENT.orange, weight: 3, fillOpacity: 0.12, dashArray: '6 6' }} />
          )}
          referenceMarkers={referenceMarkers}
        />
      </div>

      <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-gray-100 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => editorRef.current?.triggerAdd()}
            className="text-xs font-medium bg-white border border-gray-300 rounded-lg px-2.5 py-1.5 shadow-xs text-brand-cyan-text hover:bg-blue-50 shrink-0">
            + Dodaj tačku
          </button>
          {error ? <p className="text-xs text-red-600 truncate">{error}</p>
            : !canSave ? <p className="text-xs text-amber-600 truncate">Potrebno je najmanje 3 tačke za granicu (ili obrišite sve za povratak na automatski obris).</p>
            : <span className="text-xs text-gray-400 truncate">Klik na tačku je otvara za izmenu, prevlačenje je pomera.</span>}
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={onClose} className="text-sm font-medium text-gray-500 hover:text-gray-700 px-4 py-2 rounded-lg border border-gray-300">
            Otkaži
          </button>
          <button onClick={() => saveBoundaryMut.mutate()} disabled={saveBoundaryMut.isPending || !canSave}
            className="text-sm font-medium text-brand-midnight bg-brand-cyan hover:bg-brand-cyan-dark disabled:opacity-40 px-4 py-2 rounded-lg">
            Sačuvaj
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Semantic zoom (docs/plan-web-ux-cohesion.md, mobile/map-semantic-zoom's
// OSM tier ladder in mobile-rn/src/map/zoomTiers.ts) ─────────────────────────
//
// Two effective tiers for pipelines/pumps/reservoirs, not the three named in
// the design conversation: "medium" and "close" render those identically
// here (zones + facilities + pipelines, all with their core value/severity)
// -- the only difference the conversation actually described between them
// was the natural outcome of being zoomed in enough to comfortably click
// something, which isn't a rendering distinction. Disclosed simplification,
// not silently dropped scope. FAR_MAX_ZOOM matches mobile's FAR_MAX_ZOOM (11)
// -- was 12 with small unlabeled device dots at/below it; corrected per
// mobile-to-scada-2026-09-20-instrumentation-severity-gap.md, which flagged
// that spec never actually called for dots at the far tier at all. Below
// this, only zones draw -- except a device in warning/critical state, which
// breaks through at every tier (locatedDevices filter below), matching
// mobile's deviceBreaksThroughTier().
//
// Instrumentation (anything that isn't a pump or reservoir -- flow/pressure/
// level sensors, valves) is the one place the third tier is real:
// mobile-to-web-2026-09-30-map-tier-divergence.md caught web showing sensors
// at the medium tier while mobile correctly waited for close, since web had
// collapsed medium/close into one boundary for everything. CLOSE_MIN_ZOOM
// matches mobile's own constant of the same name (mobile-rn/src/map/
// zoomTiers.ts) -- instrumentationVisible() there is the same rule.
const FAR_MAX_ZOOM = 11
const CLOSE_MIN_ZOOM = 16

function ZoomTracker({ onZoomChange }: { onZoomChange: (zoom: number) => void }) {
  const map = useMapEvents({ zoomend: () => onZoomChange(map.getZoom()) })
  useEffect(() => { onZoomChange(map.getZoom()) }, [map, onZoomChange])
  return null
}

// Repurposed "Izaberi zonu" dropdown: jump-to-zone now means pan/zoom the
// live map there (it never did before -- selecting only changed what
// rendered, not what was in view), while clicking something already
// visible on the map doesn't need to re-center under it.
function FlyToHandler({ target }: { target: { center: [number, number]; zoom: number } | null }) {
  const map = useMap()
  useEffect(() => { if (target) map.flyTo(target.center, target.zoom) }, [target, map])
  return null
}

// Map pulse category/intensity classifier (reservoirPulse/pumpPulse/
// devicePulse) now lives in ../utils/pulseCategory.ts, shared with
// DemandsAttentionSection.tsx's tiles so both use the exact same
// classification instead of two independently-drifting ones -- deviceColor()
// above is untouched, still used by the pipeline/boundary editors' plain
// non-pulsing device markers, a simpler, different context.

// Disclosed placeholder, like pulseCategory.ts's FAST_RATE_M_PER_H: a 40%+
// shortfall/surplus relative to nominal reads as "fast" -- no real
// calibration data. Zone-specific (netFlow/nominalFlow only exist for
// zones), so this stays local rather than moving to the shared module.
function zonePulse(status: ZoneFlowStatus, netFlow: number | null, nominalFlow: number | null): { intensity: number } {
  if (status === 'unknown') return { intensity: 0.15 }
  if (netFlow === null || nominalFlow === null || nominalFlow === 0) {
    return { intensity: status === 'deficit' ? 0.5 : 0.15 }
  }
  return { intensity: clamp01(Math.abs(netFlow - nominalFlow) / (nominalFlow * 0.4)) }
}

// The one "core value" per node type, shown inside the marker at mid/max
// zoom -- a reservoir's depth is the number an operator actually scans
// for; a pump's flow rate if it's reporting one, else just on/off.
function coreValueLabel(d: ScadaDevice, pumpByKey: Map<string, PumpStatus>, reservoirByKey: Map<string, ReservoirStatus>): string | null {
  if (d.node_type === 'reservoir') {
    const r = reservoirByKey.get(d.device_key)
    return r?.depth_m != null ? `${r.depth_m.toFixed(1)}m` : null
  }
  if (d.node_type === 'pump') {
    const p = pumpByKey.get(d.device_key)
    if (!p) return null
    if (p.flow_lps != null) return `${p.flow_lps.toFixed(0)}`
    if (p.active === true) return 'ON'
    if (p.active === false) return 'OFF'
    return null
  }
  // flow_meter/pressure_sensor/level_sensor/valve had no marker label at
  // all before this -- GET /scada/devices' own latest_value is the same
  // generic reading ExtraReadingsSection/DeviceDetailFields already read,
  // just never surfaced here (reported live: a pressure sensor's own
  // marker and popup showed no value anywhere).
  return d.latest_value != null ? `${d.latest_value.toFixed(1)}` : null
}

// Same divIcon-with-inline-styles approach as circleIcon (Leaflet markers
// live outside React/Tailwind's own tree) -- kept as its own function
// rather than extending circleIcon, since circleIcon's many existing call
// sites (the pipeline/boundary editors, muted-device styling) don't need
// a label or a pulse and shouldn't risk a shared-function regression.
function objectMarkerIcon({ color, size, label, intensity = 0.15, muted = false, pulsing = true, fault = null }: {
  color: string
  size: number
  label?: string | null
  // Pump kvar fault: a small triangle badge on the dot's corner -- a shape,
  // not a recolor, so the status color/pulse stay exactly as they were.
  fault?: { since: string | null; critical: boolean } | null
  // 0-1, always present when pulsing -- calm at baseline rather than
  // reserved for alert states only (user's own call, unchanged for
  // zones/positive/disconnected markers).
  intensity?: number
  muted?: boolean
  // Motion reserved for critical devices (mobile's deviceShouldPulse rule,
  // routed via mobile-to-scada-2026-09-20-instrumentation-severity-gap.md):
  // a warning-severity device marker stays static so a screen of orange
  // doesn't dilute the one red thing on it. Everything else still pulses.
  pulsing?: boolean
}) {
  const border = muted ? `${size > 20 ? 3 : 2}px dashed #62697F` : `${size > 20 ? 3 : 2}px solid white`
  // Muted keeps the dot's true color but caps the ring at a calm baseline
  // regardless of actual intensity -- alerts_muted means the user asked
  // not to be shouted at, the ring shouldn't scream even if the color
  // still tells the truth.
  const effectiveIntensity = muted ? Math.min(intensity, 0.15) : clamp01(intensity)
  const duration = (2.4 - 1.7 * effectiveIntensity).toFixed(2)
  const maxSpread = Math.max(2, Math.round(size * (0.3 + 0.9 * effectiveIntensity)))
  const pulseVar = pulsing ? `--pulse-color:${color};--pulse-duration:${duration}s;--pulse-max-spread:${maxSpread}px;` : ''
  const fontSize = Math.max(9, Math.round(size / 3.2))
  const labelHtml = label
    ? `<span style="font-size:${fontSize}px;font-weight:700;color:#fff;line-height:1;white-space:nowrap;">${label}</span>`
    : ''
  const faultHtml = fault ? faultMarkerBadgeHtml({ since: fault.since, critical: fault.critical, size: size > 20 ? 16 : 12 }) : ''
  return L.divIcon({
    className: '',
    html: `<div class="${pulsing ? 'map-status-pulse' : ''}" style="${pulseVar}position:relative;width:${size}px;height:${size}px;border-radius:50%;background:${color};border:${border};box-shadow:0 1px 4px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;">${labelHtml}${faultHtml}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  })
}

type SelectedMapObject =
  | { type: 'zone'; id: string }
  | { type: 'pipeline'; id: string }
  | { type: 'device'; id: string }

// compact/readOnly/onFullscreen support the executive dashboard's
// Infrastructure focus embedding this same component at a smaller scale
// (docs/plan-executive-dashboard-focuses.md) — a read-only preview with a
// fullscreen button that mounts a second, full-size instance in a modal,
// rather than a separate map component.
export function TopologyView({ pumps, reservoirs, compact = false, readOnly = false, onFullscreen, mapHeightClass, onDeviceClick }: {
  pumps: PumpStatus[]
  reservoirs: ReservoirStatus[]
  compact?: boolean
  readOnly?: boolean
  onFullscreen?: () => void
  // Overrides the compact/default height classes below — the fullscreen
  // modal that mounts a second instance of this component needs the map
  // to actually be large, not just "not compact" (h-96 alone still reads
  // as a small map inside a big dialog).
  mapHeightClass?: string
  // Dashboard callers (DeviceDetailModal) — a selected zone's device
  // markers get a "Detalji" button in their popup when this is set.
  // Undefined here (ScadaView's own usage) leaves the popup exactly as it
  // was before.
  onDeviceClick?: (deviceKey: string, nodeType: NodeType) => void
}) {
  const { user } = useAuthStore()
  const canEditMap = (user?.roles.some((r) => ['admin', 'company_admin', 'scada_operator'].includes(r)) ?? false) && !readOnly
  const { data: zones = [] }     = useQuery({ queryKey: ['scada-zones'],      queryFn: () => scadaApi.listZones().then((r) => r.data), staleTime: 60_000 })
  const { data: devices = [] }   = useQuery({ queryKey: ['scada-devices'],    queryFn: () => scadaApi.listDevices().then((r) => r.data) })
  const { data: zoneLinks = [] } = useQuery({ queryKey: ['scada-zone-links'], queryFn: () => scadaApi.listZoneLinks().then((r) => r.data), staleTime: 60_000 })
  const { data: statuses = [] } = useQuery({
    queryKey: ['scada-zone-status'],
    queryFn:  () => scadaApi.zoneStatus().then((r) => r.data),
    refetchInterval: 30_000,
  })
  // Signal 2 from docs/plan-scada-zone-tiers-borders-attachments.md: an
  // individual device breach flares independently of the zone's own flow
  // color (Signal 1, `status` above) — and per
  // docs/plan-generic-threshold-alerts.md, flares every zone the breaching
  // device's affected_zone_ids names, not just one. Alert episodes now have
  // a real end (resolved_at), so "still active" is the open filter itself,
  // not a "fired in the last hour" proxy.
  const { data: thresholdAlerts = [] } = useQuery({
    queryKey: ['scada-threshold-breach-flare'],
    queryFn: () => scadaApi.listAlertEvents({
      alert_type: 'device_threshold_breach',
      open: true,
      limit: 200,
    }).then((r) => r.data),
    refetchInterval: 30_000,
  })
  const deviceLabelByKey = new Map(devices.map((d) => [d.device_key, d.label]))
  const flareByZone = new Map<string, { deviceLabel: string; field: string; value: unknown }[]>()
  thresholdAlerts.forEach((e) => {
    const ids = Array.isArray(e.detail.affected_zone_ids) ? e.detail.affected_zone_ids as string[] : []
    ids.forEach((zoneId) => {
      const list = flareByZone.get(zoneId) ?? []
      list.push({
        deviceLabel: deviceLabelByKey.get(e.device_key) ?? e.device_key,
        field:       String(e.detail.field ?? '?'),
        value:       e.detail.value,
      })
      flareByZone.set(zoneId, list)
    })
  })

  const [zoom, setZoom] = useState(FAR_MAX_ZOOM)
  const [selected, setSelected] = useState<SelectedMapObject | null>(null)
  const [flyTarget, setFlyTarget] = useState<{ center: [number, number]; zoom: number } | null>(null)
  const [editingLink, setEditingLink]     = useState<ScadaZoneLink | null>(null)
  const [editingBoundary, setEditingBoundary] = useState<ScadaZone | null>(null)
  const isFar = zoom <= FAR_MAX_ZOOM

  const statusByZone   = new Map(statuses.map((s) => [s.zone_id, s]))
  const pumpByKey      = new Map(pumps.map((p) => [p.device_key, p]))
  const reservoirByKey = new Map(reservoirs.map((r) => [r.device_key, r]))

  const zoneMarkers = zones
    .filter((z) => z.active)
    .map((z) => {
      const st = statusByZone.get(z.id)
      const boundary = z.boundary_polygon.length >= 3
        ? [...z.boundary_polygon].sort((a, b) => a.order - b.order).map((p): [number, number] => [p.lat, p.lng])
        : null
      const hull = zoneHull(z.id, devices)
      // A drawn boundary can outrun the zone's current device scatter (e.g.
      // covers planned expansion), so it renders even for a zone with no
      // located devices — fall back to the polygon's own centroid.
      const centroid = zoneCentroid(z.id, devices) ?? (boundary
        ? [boundary.reduce((s, p) => s + p[0], 0) / boundary.length, boundary.reduce((s, p) => s + p[1], 0) / boundary.length] as [number, number]
        : null)
      const status      = st?.status ?? 'unknown' as ZoneFlowStatus
      const zoneSeverity = st?.severity ?? 'unknown' as StatusSeverity
      const netFlow      = st?.net_flow_lps ?? null
      const nominalFlow  = st?.nominal_flow_lps ?? null

      // Signal 1 (docs/plan-scada-zone-tiers-borders-attachments.md) —
      // never actually built; the area fill has stayed on ZoneFlowStatus
      // alone. Real flow-balance data (status !== 'unknown') stays
      // authoritative when it exists — even for a well/reservoir zone, a
      // measured outflow-vs-nominal shortfall is itself meaningful (e.g.
      // less leaving the well than expected can mean the well's struggling,
      // not just the pipe). The gap is specifically when status is
      // 'unknown' — no zone-link at all, or one with no flow_meter_node_id,
      // or the meter's gone quiet for over an hour (zone_flow_statuses'
      // own definition of 'unknown') — which is structurally permanent for
      // a well/reservoir zone with no flow meter wired, not a transient
      // gap. There, borrow the zone's own defining device's health instead
      // (the exact same reservoirPulse()/pumpPulse() classification already
      // coloring that device's own marker), simpler than the doc's original
      // net_flow_lps-sign/no-outflow computation and confirmed with the
      // user as the more useful fallback in practice. Distribution/source
      // zones are untouched — their 'unknown' still means a real gap worth
      // seeing as gray, no single defining device to fall back to anyway.
      //
      // Multiple reservoirs/pumps in one zone (rare) — first found wins, no
      // aggregation with other devices in the zone; a device's own problem
      // already surfaces independently via Signal 2's flare/its own
      // pulsing marker rather than fighting for this one area color.
      // Which device stands in for the zone depends on category: a
      // reservoir zone's defining device is its reservoir; a well zone's is
      // its pump (there's usually no reservoir at a well at all) — the
      // fallback used to only ever look for a reservoir device, so a
      // well-only-a-pump zone got no Signal-1 fallback whatsoever.
      const zoneReservoir = (status === 'unknown' && z.zone_category === 'reservoir')
        ? devices.find((d) => d.zone_id === z.id && d.node_type === 'reservoir')
        : undefined
      const zonePump = (status === 'unknown' && z.zone_category === 'well')
        ? devices.find((d) => d.zone_id === z.id && d.node_type === 'pump')
        : undefined
      const fallbackPulse =
        zoneReservoir ? reservoirPulse(reservoirByKey.get(zoneReservoir.device_key)) :
        zonePump      ? pumpPulse(pumpByKey.get(zonePump.device_key)) :
        null
      const zp = fallbackPulse ? null : zonePulse(status, netFlow, nominalFlow)

      return {
        zone:        z,
        centroid,
        polygon:     boundary ?? hull,
        isDrawn:     boundary !== null,
        status, netFlow, nominalFlow,
        color:     fallbackPulse ? pulseColor(fallbackPulse)         : ZONE_SEVERITY_COLOR[zoneSeverity],
        textColor: fallbackPulse ? pulseTextColor(fallbackPulse)     : ZONE_SEVERITY_TEXT_COLOR[zoneSeverity],
        label:     fallbackPulse ? CATEGORY_ZONE_LABEL[fallbackPulse.category] : ZONE_STATUS_LABEL[status],
        intensity: fallbackPulse ? fallbackPulse.intensity : zp!.intensity,
      }
    })
    .filter((m): m is typeof m & { centroid: [number, number] } => m.centroid !== null)

  // Devices/pipelines are no longer gated behind "a zone is selected" —
  // zoom tier alone decides visibility now (docs/plan-web-ux-cohesion.md);
  // selection only decides what the top-right edit button targets.
  const locatedDevices = devices.filter((d) => d.location_lat != null && d.location_lng != null)
  // A freshly-created zone-link has no drawn path yet (path.length < 2) --
  // render-only default: a straight line between the two zones' anchors
  // (zoneAnchor above) so it isn't blank until someone opens "Uredi
  // putanju." Never written to scada_zone_link_points -- the moment a real
  // path is drawn, that replaces this synthetic line entirely. Skipped
  // (not just left blank) when either end isn't a zone (an access-point
  // endpoint) or has no resolvable anchor.
  const visibleLinks = zoneLinks
    .map((lk) => {
      // A device/access_point point with no location yet reads back null
      // (device-ownership step 2) -- skip it rather than draw a broken
      // line through it; the synthetic zone-anchor fallback below covers a
      // pipe that's lost too many of its points to draw for real.
      const located = [...lk.path].sort((a, b) => a.order - b.order)
        .filter((p): p is ScadaZoneLinkPoint & { lat: number; lng: number } => p.lat != null && p.lng != null)
      if (located.length >= 2) {
        const pts = located.map((p): [number, number] => [p.lat, p.lng])
        return { link: lk, points: pts, synthetic: false }
      }
      const from = zoneAnchor(lk.from_zone_id, zones, devices)
      const to   = zoneAnchor(lk.to_zone_id, zones, devices)
      return from && to ? { link: lk, points: [from, to], synthetic: true } : null
    })
    .filter((v): v is { link: ScadaZoneLink; points: [number, number][]; synthetic: boolean } => v !== null)

  const selectedZoneObj = selected?.type === 'zone' ? zones.find((z) => z.id === selected.id) ?? null : null
  const selectedLink    = selected?.type === 'pipeline' ? zoneLinks.find((lk) => lk.id === selected.id) ?? null : null
  // Device-location editing doesn't exist yet (a later phase) — a selected
  // device has nothing for the edit button to open yet.

  const zoneDevices   = selectedZoneObj ? locatedDevices.filter((d) => d.zone_id === selectedZoneObj.id) : []
  const zoneLinksHere = selectedZoneObj ? zoneLinks.filter((lk) => lk.from_zone_id === selectedZoneObj.id || lk.to_zone_id === selectedZoneObj.id) : []

  const centroids = zoneMarkers.map((m) => m.centroid)
  const mapCenter: [number, number] = centroids.length > 0
    ? [centroids.reduce((s, c) => s + c[0], 0) / centroids.length, centroids.reduce((s, c) => s + c[1], 0) / centroids.length]
    : FALLBACK_CENTER

  const editLabel =
    selectedZoneObj ? 'Uredi granicu' :
    selectedLink    ? 'Uredi putanju' :
    null

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-xs p-6">
      <div className="flex items-center justify-between mb-1">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Topologija mreže</h2>
          <p className="text-sm text-gray-400 mt-1">
            {selectedZoneObj ? `Zona: ${selectedZoneObj.label}`
              : selectedLink ? `Cevovod: ${selectedLink.label}`
              : 'Klik na zonu, uređaj ili cevovod za detalje'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selected && (
            <button onClick={() => setSelected(null)}
              className="text-sm font-medium text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg border border-gray-300">
              Poništi izbor
            </button>
          )}
          {zones.filter((z) => z.active).length > 0 && (
            <select
              value=""
              onChange={(e) => {
                const z = zones.find((zz) => zz.id === e.target.value)
                if (!z) return
                const c = zoneCentroid(z.id, devices)
                if (c) setFlyTarget({ center: c, zoom: 15 })
                setSelected({ type: 'zone', id: z.id })
              }}
              className="px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-600"
            >
              <option value="">Izaberi zonu…</option>
              {zones.filter((z) => z.active).map((z) => <option key={z.id} value={z.id}>{z.label}</option>)}
            </select>
          )}
          {onFullscreen && (
            <button onClick={onFullscreen}
              className="text-sm font-medium text-brand-cyan-text hover:text-brand-midnight px-3 py-1.5 rounded-lg border border-blue-200 hover:bg-blue-50">
              Pun ekran
            </button>
          )}
        </div>
      </div>

      <div className={`relative isolate ${mapHeightClass ?? (compact ? 'h-64' : 'h-96')} rounded-xl overflow-hidden mt-4`}>
        {canEditMap && editLabel && (
          <button
            onClick={() => {
              if (selectedZoneObj) setEditingBoundary(selectedZoneObj)
              else if (selectedLink) setEditingLink(selectedLink)
            }}
            className="absolute top-3 right-3 z-[1000] text-sm font-medium text-brand-cyan-text bg-white border border-gray-300 hover:bg-blue-50 px-3 py-1.5 rounded-lg shadow-xs"
          >
            {editLabel}
          </button>
        )}

        <LeafletMap center={mapCenter} zoom={zoom}>
          <ZoomTracker onZoomChange={setZoom} />
          <FlyToHandler target={flyTarget} />

          {zoneMarkers.map(({ zone, polygon, isDrawn, color }) => polygon && (
            <Polygon key={`hull-${zone.id}`} positions={polygon}
              pathOptions={{
                color, weight: 1.5, opacity: 0.7,
                fillColor: color, fillOpacity: 0.18,
                dashArray: isDrawn ? undefined : '6 6', // dashed = inferred from device scatter, unconfirmed
              }}
              eventHandlers={{ click: () => setSelected({ type: 'zone', id: zone.id }) }}
            />
          ))}

          {zoneMarkers.map(({ zone, centroid }) => flareByZone.has(zone.id) && (
            // Independent of the zone's own area-fill status color (Signal 1
            // above) — this flares purely off a device_threshold_breach
            // naming this zone in affected_zone_ids, per
            // docs/plan-generic-threshold-alerts.md. A zone can render blue
            // (flow-balanced) and still show this ring if one attached
            // sensor is out of range.
            <Circle key={`flare-${zone.id}`} center={centroid} radius={90}
              pathOptions={{ color: '#DC2626', weight: 3, fillOpacity: 0, dashArray: '4 4' }} />
          ))}

          {zoneMarkers.map(({ zone, centroid, color, textColor, label, intensity, netFlow, nominalFlow }) => {
            const flares = flareByZone.get(zone.id) ?? []
            return (
              <Marker key={zone.id} position={centroid}
                icon={objectMarkerIcon({ color, size: 22, intensity })}
                eventHandlers={{ click: () => setSelected({ type: 'zone', id: zone.id }) }}>
                <Popup>
                  <div className="text-sm">
                    <p className="font-semibold text-gray-900">{zone.label}</p>
                    <p className="font-medium mt-1" style={{ color: textColor }}>{label}</p>
                    {netFlow !== null && nominalFlow !== null && (
                      <p className="text-gray-600 mt-1">{netFlow.toFixed(1)} / {nominalFlow.toFixed(1)} L/s</p>
                    )}
                    {flares.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-red-100">
                        {flares.map((f, i) => (
                          <p key={i} className="text-brand-critical-text text-xs">
                            {f.deviceLabel} — {f.field}: {String(f.value)}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                </Popup>
              </Marker>
            )
          })}

          {locatedDevices
            .map((d) => ({ d, pulse: devicePulse(d, pumpByKey, reservoirByKey) }))
            // Far tier draws zones only, and instrumentation additionally
            // waits for the close tier (mobile's instrumentationVisible) --
            // a device breaks through both gates regardless of zoom only
            // while it's actually in trouble (category 'problem' covers both
            // warning and critical), matching mobile's deviceBreaksThroughTier().
            .filter(({ d, pulse }) => {
              if (pulse.category === 'problem') return true
              if (isFar) return false
              const isInstrumentation = d.node_type !== 'pump' && d.node_type !== 'reservoir'
              return !isInstrumentation || zoom >= CLOSE_MIN_ZOOM
            })
            .map(({ d, pulse }) => {
              // Critical pulses; warning (and everything else) stays static --
              // deviceShouldPulse's web equivalent.
              const pulsing = pulse.category !== 'problem' || pulse.severity === 'critical'
              const livePump = d.node_type === 'pump' ? pumpByKey.get(d.device_key) : undefined
              const fault = livePump?.fault === true
                ? { since: livePump.fault_since ?? null, critical: livePump.status_severity === 'critical' }
                : null
              const icon = isFar
                ? objectMarkerIcon({ color: pulseColor(pulse), size: 10, intensity: pulse.intensity, muted: d.alerts_muted, pulsing, fault })
                : objectMarkerIcon({
                    color: pulseColor(pulse), size: 28, intensity: pulse.intensity, muted: d.alerts_muted, pulsing, fault,
                    label: coreValueLabel(d, pumpByKey, reservoirByKey),
                  })
              return (
                <Marker key={d.id} position={[d.location_lat!, d.location_lng!]} icon={icon} title={fault ? faultText(fault.since) : undefined}
                  eventHandlers={{ click: () => setSelected({ type: 'device', id: d.id }) }}>
                  <Popup>
                    <div className="text-sm">
                      <p className="font-semibold text-gray-900">{d.label}</p>
                      <p className="text-gray-500 font-mono text-xs">{d.device_key}</p>
                      <p className="text-gray-600 mt-1">{NODE_TYPE_LABEL[d.node_type] ?? d.node_type}</p>
                      {d.node_type !== 'pump' && d.node_type !== 'reservoir' && (
                        <p className="text-gray-900 font-medium mt-1">
                          {d.latest_value != null ? `${d.latest_value.toFixed(2)}${d.measurement_unit ? ` ${d.measurement_unit}` : ''}` : 'Nema podataka'}
                        </p>
                      )}
                      {fault && <p className="mt-1"><FaultNotice since={fault.since} critical={fault.critical} /></p>}
                      {onDeviceClick && (
                        <button
                          onClick={() => onDeviceClick(d.device_key, d.node_type)}
                          className="mt-2 text-brand-cyan-text hover:text-brand-midnight text-xs font-medium"
                        >
                          Detalji →
                        </button>
                      )}
                    </div>
                  </Popup>
                </Marker>
              )
            })}

          {!isFar && visibleLinks.map(({ link: lk, points, synthetic }) => {
            const isSelected = selected?.type === 'pipeline' && selected.id === lk.id
            return (
              <Polyline key={lk.id} positions={points}
                pathOptions={{
                  color: ACCENT.active, weight: isSelected ? 5 : 3,
                  opacity: synthetic ? 0.45 : 0.8, dashArray: synthetic ? '6 6' : undefined,
                }}
                eventHandlers={{ click: () => setSelected({ type: 'pipeline', id: lk.id }) }}>
                <Popup>
                  {lk.label}
                  {synthetic && <><br /><span className="text-xs text-gray-400">Predložena linija — putanja još nije nacrtana</span></>}
                </Popup>
              </Polyline>
            )
          })}
        </LeafletMap>
      </div>

      {zoneMarkers.length === 0 && (
        <p className="text-sm text-gray-400 text-center py-8">Nema zona sa lociranim uređajima za prikaz na mapi.</p>
      )}

      {selectedZoneObj && (
        <>
          {zoneDevices.length === 0 && zoneLinksHere.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">Nema lociranih uređaja ni cevovoda u ovoj zoni.</p>
          )}
          {zoneLinksHere.length > 0 && (
            <div className="mt-4 border-t border-gray-100 pt-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Putanje cevovoda</p>
              <div className="space-y-2">
                {zoneLinksHere.map((lk) => (
                  <button
                    key={lk.id}
                    onClick={() => setSelected({ type: 'pipeline', id: lk.id })}
                    className={`w-full flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-left ${
                      selected?.type === 'pipeline' && selected.id === lk.id ? 'bg-blue-50 ring-1 ring-blue-200' : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{lk.label}</p>
                      <p className="text-xs text-gray-400">{lk.path.length} tačaka</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {editingLink && <PipelinePathEditorModal link={editingLink} onClose={() => setEditingLink(null)} />}
      {editingBoundary && (
        <ZoneBoundaryEditorModal
          zone={editingBoundary}
          initialCenter={zoneCentroid(editingBoundary.id, devices)}
          devices={devices}
          onClose={() => setEditingBoundary(null)}
        />
      )}
    </div>
  )
}

// ─── Shared "quick edit" grid primitives ───────────────────────────────────────

const CELL_INPUT  = 'w-full px-2 py-1.5 text-sm border border-transparent hover:border-gray-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 rounded-sm outline-hidden bg-transparent focus:bg-white transition-colors'
const CELL_SELECT = CELL_INPUT + ' cursor-pointer'
const TH = 'text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wide px-2 py-2 border-b border-gray-100 whitespace-nowrap'
const TD = 'px-2 py-1 align-middle'
const FORM_INPUT = 'w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 outline-hidden'

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg className={`w-4 h-4 text-gray-400 transition-transform shrink-0 ${open ? 'rotate-90' : ''}`}
      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  )
}

function CollapsibleSection({ title, subtitle, defaultOpen = true, controls, children }: {
  title: string
  subtitle?: string
  defaultOpen?: boolean
  controls?: React.ReactNode
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-xs overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-5 py-3.5 bg-gray-50 border-b border-gray-100 flex-wrap">
        <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 min-w-0">
          <ChevronIcon open={open} />
          <span className="font-semibold text-gray-900 text-sm whitespace-nowrap">{title}</span>
          {subtitle && <span className="text-xs text-gray-400 truncate">{subtitle}</span>}
        </button>
        {controls && <div className="flex items-center gap-2 flex-wrap">{controls}</div>}
      </div>
      {open && <div className="p-4">{children}</div>}
    </div>
  )
}

function SearchInput({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder ?? 'Pretraga…'}
      className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg w-52 focus:ring-2 focus:ring-blue-400 outline-hidden"
    />
  )
}

function TextCell({ value, onCommit, placeholder, className }: {
  value: string; onCommit: (v: string) => void; placeholder?: string; className?: string
}) {
  const [local, setLocal] = useState(value)
  useEffect(() => { setLocal(value) }, [value])
  return (
    <input
      className={className ?? CELL_INPUT}
      value={local}
      placeholder={placeholder}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => { if (local.trim() !== value) onCommit(local.trim()) }}
    />
  )
}

function NumberCell({ value, onCommit, placeholder, step = 'any', className, min, max }: {
  value: string; onCommit: (v: string) => void; placeholder?: string; step?: string; className?: string; min?: string; max?: string
}) {
  const [local, setLocal] = useState(value)
  useEffect(() => { setLocal(value) }, [value])
  return (
    <input
      type="number" step={step} min={min} max={max}
      className={className ?? CELL_INPUT}
      value={local}
      placeholder={placeholder}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => { if (local !== value) onCommit(local) }}
    />
  )
}

function SelectCell({ value, onCommit, options, className }: {
  value: string; onCommit: (v: string) => void; options: { value: string; label: string }[]; className?: string
}) {
  return (
    <select className={className ?? CELL_SELECT} value={value} onChange={(e) => onCommit(e.target.value)}>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  )
}

function ToggleCell({ checked, onCommit }: { checked: boolean; onCommit: (v: boolean) => void }) {
  return (
    <input type="checkbox" checked={checked} onChange={(e) => onCommit(e.target.checked)}
      className="w-4 h-4 rounded-sm border-gray-300 text-brand-cyan-text focus:ring-blue-400 cursor-pointer" />
  )
}

function apiError(e: unknown, fallback: string): string {
  return (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? fallback
}

// emptyLabel: null omits the empty option entirely -- for a required zone
// select (device/access-point owner, migration 055/decision doc), there's
// no valid "none" to offer.
function zoneOptions(zones: ScadaZone[], emptyLabel: string | null = '— Bez zone —') {
  return [
    ...(emptyLabel !== null ? [{ value: '', label: emptyLabel }] : []),
    ...zones.filter((z) => z.active).map((z) => ({ value: z.id, label: `[${ZONE_CAT_LABEL[z.zone_category]}] ${z.label}` })),
  ]
}

function accessPointOptions(accessPoints: ScadaAccessPoint[], emptyLabel = '— Bez pristupne tačke —') {
  return [
    { value: '', label: emptyLabel },
    ...accessPoints.filter((ap) => ap.active).map((ap) => ({ value: ap.id, label: ap.label })),
  ]
}

// ─── Uređaji (devices) grid ─────────────────────────────────────────────────────

type DeviceDraft = { node_type: NodeType; label: string; zone_id: string }

const MODBUS_KNOWN_FIELDS = ['running', 'depth_m', 'flow_lps', 'pressure_kpa']
const MODBUS_KNOWN_SENSOR_TYPES = ['level_sensor', 'pressure_sensor']
const MODBUS_TYPE_LABEL: Record<ModbusRegisterType, string> = {
  holding_register: 'Holding register',
  coil:              'Coil',
  input_register:    'Input register (samo čitanje)',
  discrete_input:    'Discrete input (samo čitanje)',
}

function readingsEqual(a: ModbusReading[], b: ModbusReading[]) {
  return JSON.stringify(a) === JSON.stringify(b)
}

// Real Modbus protocol limits, not arbitrary form defaults — addresses are
// 0-based on the wire (0 is valid), unit IDs are 1–247 (0 is broadcast-only,
// 248–255 reserved). See handoffs/overwatch-to-web-2026-08-28-modbus-field-
// validation.md.
function isValidModbusAddress(n: number): boolean {
  return Number.isInteger(n) && n >= 0 && n <= 65535
}
function isValidUnitId(n: number): boolean {
  return Number.isInteger(n) && n >= 1 && n <= 247
}

// Mirrors backend/app/api/scada.py's _validate_identifier exactly — a field
// name with a literal space made it all the way from this form into a
// generated config.yaml deployment package before the backend validator
// existed (found live, device "b1", 2026-09). Catch it here too so the
// mistake shows up as an inline form error, not a 422 after "Sačuvaj".
const IDENTIFIER_RE = /^[a-zA-Z][a-zA-Z0-9_]{0,63}$/
function isValidIdentifier(v: string): boolean {
  return IDENTIFIER_RE.test(v)
}

function ModbusRegistersEditor({ device, onCommit }: { device: ScadaDevice; onCommit: (body: Partial<ScadaDevice>) => void }) {
  const [rows, setRows] = useState<ModbusReading[]>(device.modbus_readings)
  const [error, setError] = useState('')
  useEffect(() => { setRows(device.modbus_readings); setError('') }, [device.modbus_readings])

  const dirty = !readingsEqual(rows, device.modbus_readings)

  const updateRow = (i: number, patch: Partial<ModbusReading>) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))
  const addRow = () => setRows((rs) => [...rs, { field: '', type: 'holding_register', address: 1 }])
  const removeRow = (i: number) => setRows((rs) => rs.filter((_, idx) => idx !== i))

  const save = () => {
    for (const r of rows) {
      if (!r.field.trim()) { setError('Polje je obavezno za sve registre.'); return }
      if (!isValidIdentifier(r.field.trim())) {
        setError(`Polje mora početi slovom i sadržati samo slova, brojeve ili donju crtu (${r.field}).`)
        return
      }
      if (r.sensor_type?.trim() && !isValidIdentifier(r.sensor_type.trim())) {
        setError(`Tip senzora mora početi slovom i sadržati samo slova, brojeve ili donju crtu (${r.sensor_type}).`)
        return
      }
      if (!isValidModbusAddress(r.address)) { setError(`Adresa mora biti ceo broj 0–65535 (${r.field}).`); return }
    }
    setError('')
    onCommit({
      modbus_readings: rows.map((r) => ({
        ...r,
        field: r.field.trim(),
        sensor_type: r.sensor_type?.trim() || undefined,
      })),
    })
  }

  return (
    <div className="mt-4 pt-4 border-t border-gray-100">
      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Registri</p>
      <datalist id="modbus-field-options">
        {MODBUS_KNOWN_FIELDS.map((f) => <option key={f} value={f} />)}
      </datalist>
      <datalist id="modbus-sensor-type-options">
        {MODBUS_KNOWN_SENSOR_TYPES.map((t) => <option key={t} value={t} />)}
      </datalist>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={TH}>Polje</th>
              <th className={TH}>Tip registra</th>
              <th className={TH}>Adresa</th>
              <th className={TH}>Skala</th>
              <th className={TH}>Tip senzora</th>
              <th className={TH}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-gray-50">
                <td className={TD}>
                  <input list="modbus-field-options" className={CELL_INPUT} value={r.field}
                    onChange={(e) => updateRow(i, { field: e.target.value })} placeholder="npr. flow_lps" />
                </td>
                <td className={TD}>
                  <select className={CELL_SELECT} value={r.type}
                    onChange={(e) => updateRow(i, { type: e.target.value as ModbusRegisterType })}>
                    {(Object.entries(MODBUS_TYPE_LABEL) as [ModbusRegisterType, string][]).map(([t, l]) => (
                      <option key={t} value={t}>{l}</option>
                    ))}
                  </select>
                </td>
                <td className={TD}>
                  <input type="number" step="1" min="0" max="65535" className={CELL_INPUT} value={r.address}
                    onChange={(e) => updateRow(i, { address: e.target.value === '' ? 0 : parseInt(e.target.value, 10) })} />
                </td>
                <td className={TD}>
                  <input type="number" step="any" className={CELL_INPUT} value={r.scale ?? ''}
                    placeholder="—"
                    onChange={(e) => updateRow(i, { scale: e.target.value === '' ? undefined : parseFloat(e.target.value) })} />
                </td>
                <td className={TD}>
                  <input list="modbus-sensor-type-options" className={CELL_INPUT} value={r.sensor_type ?? ''}
                    placeholder="opciono"
                    onChange={(e) => updateRow(i, { sensor_type: e.target.value || undefined })} />
                </td>
                <td className={TD}>
                  <button onClick={() => removeRow(i)} className="text-red-500 hover:text-red-600 text-xs">Obriši</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <p className="text-xs text-gray-400 py-2">Nema definisanih registara.</p>}
      </div>
      <p className="text-[11px] text-gray-500 mt-1.5">
        Tip senzora je opcioni slobodan tekst — ako dva ili više registara na istom uređaju dele isti tip,
        sistem automatski upozorava kad njihove vrednosti previše odstupaju jedna od druge.
      </p>
      <div className="flex items-center gap-3 mt-2">
        <button onClick={addRow} className="text-sm font-medium text-brand-cyan-text hover:text-brand-midnight">+ Dodaj</button>
        {dirty && (
          <>
            <button onClick={save} className="text-sm font-medium bg-brand-cyan hover:bg-brand-cyan-dark text-brand-midnight px-3 py-1 rounded-lg">
              Sačuvaj registre
            </button>
            <button onClick={() => { setRows(device.modbus_readings); setError('') }} className="text-sm text-gray-500 hover:text-gray-700">
              Otkaži
            </button>
          </>
        )}
      </div>
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  )
}

function DeviceLocationClickHandler({ active, onClick }: { active: boolean; onClick: (ll: { lat: number; lng: number }) => void }) {
  useMapEvents({ click: (e) => { if (active) onClick({ lat: e.latlng.lat, lng: e.latlng.lng }) } })
  return null
}

// Phase 1 — the weakest of the three location-editing surfaces (no map at
// all, just bare number inputs) gets a single-marker map. Not built on
// DockedMapEditor: that component's add/delete/index-selection machinery is
// for a *list* of points, and there's only ever one here, so a plain draggable
// marker + click-to-place is the whole interaction, no panel/selection needed.
// Tap-to-place default mirrors the same reasoning DockedMapEditor's does (a
// stray click shouldn't silently relocate an already-placed device) but flips
// when there's nothing placed yet to protect.
function DeviceLocationEditor({ device, onCommit }: { device: ScadaDevice; onCommit: (body: Partial<ScadaDevice>) => void }) {
  const { location_lat: lat, location_lng: lng } = device
  const hasLocation = lat != null && lng != null
  const [tapPlace, setTapPlace] = useState(!hasLocation)
  const [flyTarget, setFlyTarget] = useState<{ center: [number, number]; zoom: number } | null>(null)
  const center: [number, number] = hasLocation ? [lat, lng] : FALLBACK_CENTER

  const commitLocation = (newLat: number, newLng: number) => onCommit({ location_lat: newLat, location_lng: newLng })

  return (
    <>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-[11px] text-gray-500 mb-1">Latitude</label>
          <NumberCell
            className={`${FORM_INPUT} w-32`}
            placeholder="44.265000"
            value={lat != null ? String(lat) : ''}
            onCommit={(v) => onCommit({ location_lat: v === '' ? null : parseFloat(v) })}
          />
        </div>
        <div>
          <label className="block text-[11px] text-gray-500 mb-1">Longitude</label>
          <NumberCell
            className={`${FORM_INPUT} w-32`}
            placeholder="20.085000"
            value={lng != null ? String(lng) : ''}
            onCommit={(v) => onCommit({ location_lng: v === '' ? null : parseFloat(v) })}
          />
        </div>
        <button
          onClick={() => navigator.geolocation?.getCurrentPosition(
            (pos) => {
              const { latitude, longitude } = pos.coords
              commitLocation(latitude, longitude)
              setFlyTarget({ center: [latitude, longitude], zoom: 16 })
            },
            () => alert('Nije moguće očitati GPS lokaciju.'),
          )}
          className="px-3 py-1.5 text-xs font-medium bg-blue-50 hover:bg-blue-100 text-brand-cyan-text rounded-lg border border-blue-200 transition-colors">
          Moja lokacija
        </button>
      </div>

      <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer mt-2.5">
        <input type="checkbox" checked={tapPlace} onChange={(e) => setTapPlace(e.target.checked)} className="rounded-sm" />
        Klikni na mapu za {hasLocation ? 'pomeranje' : 'postavljanje'} lokacije
      </label>
      <div className="h-56 rounded-lg overflow-hidden border border-gray-200 mt-1.5">
        <LeafletMap center={center} zoom={hasLocation ? 16 : 12}>
          <FlyToHandler target={flyTarget} />
          <DeviceLocationClickHandler active={tapPlace} onClick={(ll) => commitLocation(ll.lat, ll.lng)} />
          {hasLocation && (
            <Marker
              position={[lat, lng]}
              icon={pointIcon(ACCENT.active)}
              draggable
              eventHandlers={{
                dragend: (e) => {
                  const ll = (e.target as L.Marker).getLatLng()
                  commitLocation(ll.lat, ll.lng)
                },
              }}
            />
          )}
        </LeafletMap>
      </div>
    </>
  )
}

function DeviceDetailFields({ device, accessPoints, onCommit }: { device: ScadaDevice; accessPoints: ScadaAccessPoint[]; onCommit: (body: Partial<ScadaDevice>) => void }) {
  const [unitIdError, setUnitIdError] = useState('')
  const fields: { key: keyof ScadaDevice; label: string; show: boolean }[] = [
    { key: 'expected_value',        label: 'Normalna vrednost',                  show: true },
    { key: 'warn_low',              label: 'Upozorenje — minimum',               show: true },
    { key: 'warn_high',             label: 'Upozorenje — maksimum',              show: true },
    { key: 'alert_low',             label: 'Alarm — minimum',                    show: true },
    { key: 'alert_high',            label: 'Alarm — maksimum',                   show: true },
    { key: 'critical_depth_m',      label: 'Min. operativna dubina (m)',         show: device.node_type === 'reservoir' },
    { key: 'alert_offline_minutes', label: 'Prag alarma — nema signala (min)',   show: true },
    { key: 'alert_stopped_minutes', label: 'Prag alarma — pumpa stoji (min)',    show: device.node_type === 'pump' },
    { key: 'heartbeat_seconds',     label: 'Heartbeat (s)',                      show: false },
    { key: 'poll_interval_seconds', label: 'Interval očitavanja (s)',            show: true },
    { key: 'deadband',              label: 'Deadband',                           show: true },
  ]
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {fields.filter((f) => f.show).map((f) => {
          const raw = device[f.key]
          return (
            <div key={f.key}>
              <label className="block text-[11px] text-gray-500 mb-1">{f.label}</label>
              <NumberCell
                className={FORM_INPUT}
                value={raw != null ? String(raw) : ''}
                onCommit={(v) => onCommit({ [f.key]: v === '' ? null : parseFloat(v) } as Partial<ScadaDevice>)}
              />
            </div>
          )
        })}
      </div>

      <div className="mt-4 pt-4 border-t border-gray-100">
        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Alarmi</p>
        <div className="flex items-center gap-2">
          <ToggleCell checked={device.alerts_muted} onCommit={(v) => onCommit({ alerts_muted: v })} />
          <span className="text-sm text-gray-700">Utišaj alarme/push notifikacije za ovaj uređaj</span>
        </div>
        {device.alerts_muted && (
          <p className="text-[11px] text-gray-500 mt-1.5">
            Utišano od: {device.alerts_muted_at ? new Date(device.alerts_muted_at).toLocaleString('sr-Latn-RS') : '—'}
          </p>
        )}
        <p className="text-[10px] text-gray-400 mt-1">
          Uređaj i dalje prijavljuje podatke i vidljiv je u nadzoru — samo se alarmi/push obaveštenja ne šalju dok je utišan.
        </p>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-100">
        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Lokacija</p>
        <DeviceLocationEditor device={device} onCommit={onCommit} />
        <p className="text-[10px] text-gray-400 mt-1">Koristi se za centriranje zone i prikaz uređaja na topologiji mreže</p>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-100">
        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Pristupna tačka</p>
        <SelectCell
          className={`${FORM_INPUT} w-64`}
          value={device.access_point_id ?? ''}
          onCommit={(v) => onCommit({ access_point_id: v || null })}
          options={accessPointOptions(accessPoints)}
        />
      </div>

      <div className="mt-4 pt-4 border-t border-gray-100">
        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Modbus veza</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Adresa uređaja (Unit ID)</label>
            <NumberCell
              className={FORM_INPUT}
              step="1"
              min="1"
              max="247"
              placeholder="1"
              value={device.modbus_unit_id != null ? String(device.modbus_unit_id) : ''}
              onCommit={(v) => {
                if (v === '') { setUnitIdError(''); onCommit({ modbus_unit_id: null }); return }
                const n = parseInt(v, 10)
                if (!isValidUnitId(n)) { setUnitIdError('Adresa uređaja (Unit ID) mora biti ceo broj 1–247.'); return }
                setUnitIdError('')
                onCommit({ modbus_unit_id: n })
              }}
            />
            <p className="text-[10px] text-gray-400 mt-1">Modbus slave adresa koju SCADA master koristi za ovaj uređaj</p>
            {unitIdError && <p className="text-[11px] text-red-600 mt-1">{unitIdError}</p>}
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Adresiranje registara</label>
            <select
              className={FORM_INPUT}
              value={device.modbus_address_base}
              onChange={(e) => onCommit({ modbus_address_base: Number(e.target.value) as 0 | 1 })}
            >
              <option value={0}>Baza 0 (standardno)</option>
              <option value={1}>Baza 1 (kao u PLC dokumentaciji)</option>
            </select>
            <p className="text-[10px] text-gray-400 mt-1">Ako PLC dokumentacija numeriše registre od 1 umesto od 0, uključite ovu opciju</p>
          </div>
        </div>
      </div>

      {device.modbus_unit_id != null && <ModbusRegistersEditor device={device} onCommit={onCommit} />}
    </div>
  )
}

function UredajiGrid() {
  const qc = useQueryClient()
  const { data: devices = [], isError, error } = useQuery({ queryKey: ['scada-devices'], queryFn: () => scadaApi.listDevices().then((r) => r.data) })
  const { data: zones = [] }   = useQuery({ queryKey: ['scada-zones'],   queryFn: () => scadaApi.listZones().then((r) => r.data), staleTime: 60_000 })
  const { data: accessPoints = [] } = useQuery({ queryKey: ['scada-access-points'], queryFn: () => scadaApi.listAccessPoints().then((r) => r.data), staleTime: 60_000 })
  const { data: zoneLinks = [] } = useQuery({ queryKey: ['scada-zone-links'], queryFn: () => scadaApi.listZoneLinks().then((r) => r.data), staleTime: 60_000 })

  // A device's zone (owner) and pipe attachment are independent now
  // (docs/decision-device-ownership-and-attachment.md) -- this cross-
  // references it to the pipe it's on regardless, so both facts show
  // together in the grid below.
  const pipeLabelByDeviceId = new Map<string, string>()
  zoneLinks.forEach((lk) => lk.path.forEach((p) => {
    if (p.point_type === 'device' && p.device_id) pipeLabelByDeviceId.set(p.device_id, lk.label)
  }))

  const [search, setSearch]           = useState('')
  const [zoneFilter, setZoneFilter]   = useState('')
  const [typeFilter, setTypeFilter]   = useState('')
  const [statusFilter, setStatusFilter] = useState<'' | 'active' | 'inactive'>('')
  const [expanded, setExpanded]     = useState<string | null>(null)
  const [selected, setSelected]     = useState<Set<string>>(new Set())
  const [draft, setDraft]           = useState<DeviceDraft | null>(null)
  const [draftError, setDraftError] = useState('')
  const [createdKey, setCreatedKey] = useState<string | null>(null)

  const zoneLabel = (zoneId: string | null) => zones.find((z) => z.id === zoneId)?.label ?? 'Bez zone'

  const filtered = devices
    .filter((d) =>
      (!search.trim() ||
        d.label.toLowerCase().includes(search.toLowerCase()) ||
        d.device_key.toLowerCase().includes(search.toLowerCase())) &&
      (!zoneFilter || d.zone_id === zoneFilter) &&
      (!typeFilter || d.node_type === typeFilter) &&
      (!statusFilter || (statusFilter === 'active' ? d.active : !d.active))
    )
    .sort((a, b) => zoneLabel(a.zone_id).localeCompare(zoneLabel(b.zone_id)) || a.label.localeCompare(b.label))

  const patch = (key: string, body: Partial<ScadaDevice>) => {
    scadaApi.updateDevice(key, body)
      .then(() => {
        qc.invalidateQueries({ queryKey: ['scada-devices'] })
        qc.invalidateQueries({ queryKey: ['scada-latest'] })
      })
      .catch((e: unknown) => alert(apiError(e, 'Greška pri čuvanju izmene.')))
  }

  const toggleSel = (key: string) => setSelected((s) => {
    const n = new Set(s)
    if (n.has(key)) n.delete(key); else n.add(key)
    return n
  })

  const createMut = useMutation({
    mutationFn: (d: DeviceDraft) => scadaApi.createDevice({
      node_type:  d.node_type,
      label:      d.label.trim(),
      zone_id:    d.zone_id,
    } as Parameters<typeof scadaApi.createDevice>[0]),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['scada-devices'] })
      qc.invalidateQueries({ queryKey: ['scada-latest'] })
      setDraft(null); setDraftError('')
      setCreatedKey(r.data.device_key)
    },
    onError: (e: unknown) => setDraftError(apiError(e, 'Greška pri čuvanju')),
  })

  const bulkDeleteMut = useMutation({
    mutationFn: async (keys: string[]) => {
      const results = await Promise.allSettled(keys.map((k) => scadaApi.deleteDevice(k)))
      return results
        .map((r, i) => ({ r, key: keys[i] }))
        .filter((x): x is { r: PromiseRejectedResult; key: string } => x.r.status === 'rejected')
    },
    onSuccess: (failures) => {
      qc.invalidateQueries({ queryKey: ['scada-devices'] })
      qc.invalidateQueries({ queryKey: ['scada-latest'] })
      setSelected(new Set())
      if (failures.length > 0) {
        const lines = failures.map(({ r, key }) => `${key}: ${apiError(r.reason, 'Greška pri brisanju')}`)
        alert(`Neki uređaji nisu obrisani:\n${lines.join('\n')}`)
      }
    },
  })

  return (
    <CollapsibleSection
      title="Uređaji"
      subtitle={`${devices.length} — brisanje moguće samo ako uređaj nema istoriju očitavanja`}
      controls={<>
        <select className="px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg" value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)}>
          <option value="">Sve zone</option>
          {zones.filter((z) => z.active).map((z) => <option key={z.id} value={z.id}>{z.label}</option>)}
        </select>
        <select className="px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">Svi tipovi</option>
          {Object.entries(NODE_TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as '' | 'active' | 'inactive')}>
          <option value="">Svi statusi</option>
          <option value="active">Aktivni</option>
          <option value="inactive">Neaktivni</option>
        </select>
        <SearchInput value={search} onChange={setSearch} placeholder="Ključ ili naziv…" />
      </>}
    >
      {isError && (
        <p className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          Lista uređaja nije mogla da se učita — {apiError(error, 'nepoznata greška')}. Uređivanje i brisanje nisu dostupni dok se ovo ne reši.
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={TH}></th>
              <th className={TH}></th>
              <th className={TH}>Ključ</th>
              <th className={TH}>Tip</th>
              <th className={TH}>Naziv</th>
              <th className={TH}>Zona</th>
              <th className={TH}>Jedinica</th>
              <th className={TH}>Aktivan</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((d, i) => (
              <Fragment key={d.id}>
                {(i === 0 || zoneLabel(d.zone_id) !== zoneLabel(filtered[i - 1].zone_id)) && (
                  <tr>
                    <td colSpan={8} className="px-2 pt-3 pb-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
                      {zoneLabel(d.zone_id)}
                    </td>
                  </tr>
                )}
                <tr className="border-b border-gray-50 hover:bg-gray-50/60">
                  <td className={TD}>
                    <input type="checkbox" checked={selected.has(d.device_key)} onChange={() => toggleSel(d.device_key)} className="w-4 h-4 rounded-sm border-gray-300" />
                  </td>
                  <td className={TD}>
                    <button onClick={() => setExpanded(expanded === d.id ? null : d.id)} className="text-gray-400 hover:text-gray-600" title="Pragovi i alarmi">
                      <ChevronIcon open={expanded === d.id} />
                    </button>
                  </td>
                  <td className={`${TD} font-mono text-xs text-gray-500`}>{d.device_key}</td>
                  <td className={`${TD} text-sm text-gray-600`}>{NODE_TYPE_LABEL[d.node_type] ?? d.node_type}</td>
                  <td className={TD}>
                    <div className="flex items-center gap-1.5">
                      <TextCell value={d.label} onCommit={(v) => v && patch(d.device_key, { label: v })} />
                      {d.alerts_muted && (
                        <Badge className="shrink-0 bg-slate-100 text-slate-600 border border-dashed border-slate-400">
                          Utišano
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className={TD}>
                    <SelectCell value={d.zone_id} onCommit={(v) => patch(d.device_key, { zone_id: v })} options={zoneOptions(zones, null)} />
                    {pipeLabelByDeviceId.has(d.id) && (
                      <p className="text-[10px] text-gray-400 mt-0.5">Na cevovodu: {pipeLabelByDeviceId.get(d.id)}</p>
                    )}
                  </td>
                  <td className={TD}>
                    <SelectCell value={d.measurement_unit ?? ''} onCommit={(v) => patch(d.device_key, { measurement_unit: v || null })}
                      options={[{ value: '', label: '—' }, ...UNIT_OPTIONS]} />
                  </td>
                  <td className={TD}><ToggleCell checked={d.active} onCommit={(v) => patch(d.device_key, { active: v })} /></td>
                </tr>
                {expanded === d.id && (
                  <tr className="bg-gray-50/60 border-b border-gray-100">
                    <td colSpan={8} className="px-4 py-4">
                      <DeviceDetailFields device={d} accessPoints={accessPoints} onCommit={(body) => patch(d.device_key, body)} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="text-sm text-gray-400 py-4 text-center">Nema uređaja.</p>}
      </div>

      {createdKey !== null && (
        <div className="mt-3 border border-green-200 bg-green-50/60 rounded-lg px-3 py-2 flex items-center gap-2 text-sm">
          <span className="text-gray-600">Uređaj kreiran. Ključ:</span>
          <code className="font-mono text-xs bg-white border border-green-200 rounded-sm px-1.5 py-0.5">{createdKey}</code>
          <button onClick={() => navigator.clipboard.writeText(createdKey)}
            className="text-brand-cyan-text hover:text-brand-midnight underline underline-offset-2 text-xs">
            Kopiraj
          </button>
          <button onClick={() => setCreatedKey(null)}
            className="text-gray-400 hover:text-gray-600 ml-auto" title="Zatvori">
            ✕
          </button>
        </div>
      )}

      <div className="flex items-center gap-3 mt-3">
        {draft === null && (
          <button onClick={() => { setDraft({ node_type: 'pump', label: '', zone_id: '' }); setCreatedKey(null) }}
            className="text-sm font-medium text-brand-cyan-text hover:text-brand-midnight">
            + Dodaj red
          </button>
        )}
        {selected.size > 0 && (
          <button onClick={() => { if (confirm(`Obrisati ${selected.size} uređaja? Uređaji sa istorijom očitavanja neće biti obrisani — deaktivirajte ih umesto toga.`)) bulkDeleteMut.mutate([...selected]) }}
            className="text-sm font-medium text-red-600 hover:text-red-700 ml-auto">
            Ukloni izabrano ({selected.size})
          </button>
        )}
      </div>

      {draft !== null && (
        <div className="mt-3 border border-blue-200 bg-blue-50/50 rounded-lg p-3 flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Tip *</label>
            <select className={`${FORM_INPUT} w-52`} value={draft.node_type}
              onChange={(e) => setDraft((f) => f && { ...f, node_type: e.target.value as NodeType })}>
              {Object.entries(NODE_TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Naziv *</label>
            <input className={`${FORM_INPUT} w-48`} value={draft.label}
              onChange={(e) => setDraft((f) => f && { ...f, label: e.target.value })} placeholder="npr. Bunar B1" autoFocus />
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Zona *</label>
            <select className={`${FORM_INPUT} w-44`} value={draft.zone_id}
              onChange={(e) => setDraft((f) => f && { ...f, zone_id: e.target.value })}>
              <option value="" disabled>— Izaberite zonu —</option>
              {zoneOptions(zones, null).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="flex gap-1.5">
            <button onClick={() => draft.label.trim() && draft.zone_id && createMut.mutate(draft)}
              disabled={createMut.isPending || !draft.label.trim() || !draft.zone_id}
              className="w-8 h-8 rounded-lg bg-brand-cyan hover:bg-brand-cyan-dark disabled:opacity-40 text-brand-midnight flex items-center justify-center" title="Sačuvaj">
              ✓
            </button>
            <button onClick={() => { setDraft(null); setDraftError('') }}
              className="w-8 h-8 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-100 flex items-center justify-center" title="Otkaži">
              ✕
            </button>
          </div>
          {draftError && <p className="text-xs text-red-600 w-full">{draftError}</p>}
        </div>
      )}
    </CollapsibleSection>
  )
}

// ─── SCADA zone grid ─────────────────────────────────────────────────────────

type ZoneDraft = { label: string; zone_category: ZoneCategory; description: string }

function ScadaZoneGrid() {
  const qc = useQueryClient()
  const { data: zones = [] }   = useQuery({ queryKey: ['scada-zones'],   queryFn: () => scadaApi.listZones().then((r) => r.data), staleTime: 60_000 })
  const { data: devices = [] } = useQuery({ queryKey: ['scada-devices'], queryFn: () => scadaApi.listDevices().then((r) => r.data) })

  const [search, setSearch]             = useState('')
  const [categoryFilter, setCategoryFilter] = useState<ZoneCategory | ''>('')
  const [showInactive, setShowInactive] = useState(false)
  const [selected, setSelected]         = useState<Set<string>>(new Set())
  const [draft, setDraft]               = useState<ZoneDraft | null>(null)
  const [draftError, setDraftError]     = useState('')
  const [editingBoundaryZone, setEditingBoundaryZone] = useState<ScadaZone | null>(null)

  const visible = zones
    .filter((z) =>
      (showInactive || z.active) &&
      (!search.trim() || z.label.toLowerCase().includes(search.toLowerCase())) &&
      (!categoryFilter || z.zone_category === categoryFilter)
    )
    .sort((a, b) => SCADA_CAT_ORDER.indexOf(a.zone_category) - SCADA_CAT_ORDER.indexOf(b.zone_category) || a.label.localeCompare(b.label))
  const deviceCount = (zoneId: string) => devices.filter((d) => d.zone_id === zoneId).length

  const patch = (id: string, body: Partial<Pick<ScadaZone, 'label' | 'zone_category' | 'description' | 'active'>>) => {
    scadaApi.updateZone(id, body)
      .then(() => qc.invalidateQueries({ queryKey: ['scada-zones'] }))
      .catch((e: unknown) => alert(apiError(e, 'Greška pri čuvanju izmene.')))
  }
  const createMut = useMutation({
    mutationFn: (d: ZoneDraft) => scadaApi.createZone({ label: d.label.trim(), zone_category: d.zone_category, description: d.description.trim() || undefined }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['scada-zones'] }); setDraft(null); setDraftError('') },
    onError: () => setDraftError('Greška pri čuvanju'),
  })
  // 409 (zone still owns devices, device-ownership model) is a real,
  // expected outcome now -- reported per zone like device/access-point
  // bulk delete already do, never a silent "delete anyway".
  const bulkDeleteMut = useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(ids.map((id) => scadaApi.deleteZone(id)))
      return results
        .map((r, i) => ({ r, id: ids[i] }))
        .filter((x): x is { r: PromiseRejectedResult; id: string } => x.r.status === 'rejected')
    },
    onSuccess: (failures) => {
      qc.invalidateQueries({ queryKey: ['scada-zones'] })
      setSelected(new Set())
      if (failures.length > 0) {
        const lines = failures.map(({ r, id }) =>
          `${zones.find((z) => z.id === id)?.label ?? id}: ${apiError(r.reason, 'Greška pri brisanju')}`)
        alert(`Neke zone nisu obrisane:\n${lines.join('\n')}`)
      }
    },
  })
  const toggleSel = (id: string) => setSelected((s) => {
    const n = new Set(s)
    if (n.has(id)) n.delete(id); else n.add(id)
    return n
  })

  return (
    <CollapsibleSection
      title="SCADA zone"
      subtitle="Bunari, rezervoari i distributivna područja"
      controls={<>
        <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer select-none">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Prikaži neaktivne
        </label>
        <select className="px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value as ZoneCategory | '')}>
          <option value="">Sve kategorije</option>
          {SCADA_CAT_ORDER.map((c) => <option key={c} value={c}>{ZONE_CAT_LABEL[c]}</option>)}
        </select>
        <SearchInput value={search} onChange={setSearch} placeholder="Naziv…" />
      </>}
    >
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={TH}><span className="sr-only">Izaberi</span></th>
              <th className={TH}>Naziv</th>
              <th className={TH}>Kategorija</th>
              <th className={TH}>Opis</th>
              <th className={TH}>Uređaji</th>
              <th className={TH}>Aktivna</th>
              <th className={TH}>Granica</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((z) => (
              <tr key={z.id} className={`border-b border-gray-50 hover:bg-gray-50/60 ${!z.active ? 'opacity-50' : ''}`}>
                <td className={TD}>
                  <input type="checkbox" checked={selected.has(z.id)} onChange={() => toggleSel(z.id)} className="w-4 h-4 rounded-sm border-gray-300" />
                </td>
                <td className={TD}><TextCell value={z.label} onCommit={(v) => v && patch(z.id, { label: v })} /></td>
                <td className={TD}>
                  <SelectCell value={z.zone_category} onCommit={(v) => patch(z.id, { zone_category: v as ZoneCategory })}
                    options={SCADA_CAT_ORDER.map((c) => ({ value: c, label: ZONE_CAT_LABEL[c] }))} />
                </td>
                {/* null, not undefined -- /scada/zones/{id} is exclude_unset (see
                    scada.py's own "Fixes the reported bug" comment), so an
                    omitted key means "leave alone" and never actually clears. */}
                <td className={TD}><TextCell value={z.description ?? ''} onCommit={(v) => patch(z.id, { description: v || null })} /></td>
                <td className={`${TD} text-sm text-gray-500 text-center`}>{deviceCount(z.id)}</td>
                <td className={TD}><ToggleCell checked={z.active} onCommit={(v) => patch(z.id, { active: v })} /></td>
                <td className={TD}>
                  <button onClick={() => setEditingBoundaryZone(z)} className="text-xs font-medium text-brand-poi-text hover:text-brand-midnight whitespace-nowrap">
                    {z.boundary_polygon.length >= 3 ? `Uredi granicu (${z.boundary_polygon.length})` : 'Nacrtaj granicu'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {visible.length === 0 && <p className="text-sm text-gray-400 py-4 text-center">Nema SCADA zona.</p>}
      </div>

      <div className="flex items-center gap-3 mt-3">
        {draft === null && (
          <button onClick={() => setDraft({ label: '', zone_category: 'reservoir', description: '' })}
            className="text-sm font-medium text-brand-cyan-text hover:text-brand-midnight">+ Dodaj red</button>
        )}
        {selected.size > 0 && (
          <button onClick={() => { if (confirm(`Obrisati ${selected.size} zona?`)) bulkDeleteMut.mutate([...selected]) }}
            className="text-sm font-medium text-red-600 hover:text-red-700 ml-auto">
            Ukloni izabrano ({selected.size})
          </button>
        )}
      </div>

      {draft !== null && (
        <div className="mt-3 border border-blue-200 bg-blue-50/50 rounded-lg p-3 flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Naziv *</label>
            <input className={`${FORM_INPUT} w-52`} value={draft.label}
              onChange={(e) => setDraft((f) => f && { ...f, label: e.target.value })} placeholder="npr. Rezervoar Razbojište" autoFocus />
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Kategorija *</label>
            <select className={`${FORM_INPUT} w-44`} value={draft.zone_category}
              onChange={(e) => setDraft((f) => f && { ...f, zone_category: e.target.value as ZoneCategory })}>
              {SCADA_CAT_ORDER.map((c) => <option key={c} value={c}>{ZONE_CAT_LABEL[c]}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Opis</label>
            <input className={`${FORM_INPUT} w-52`} value={draft.description}
              onChange={(e) => setDraft((f) => f && { ...f, description: e.target.value })} placeholder="Kratki opis zone" />
          </div>
          <div className="flex gap-1.5">
            <button onClick={() => draft.label.trim() && createMut.mutate(draft)} disabled={createMut.isPending || !draft.label.trim()}
              className="w-8 h-8 rounded-lg bg-brand-cyan hover:bg-brand-cyan-dark disabled:opacity-40 text-brand-midnight flex items-center justify-center">✓</button>
            <button onClick={() => { setDraft(null); setDraftError('') }}
              className="w-8 h-8 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-100 flex items-center justify-center">✕</button>
          </div>
          {draftError && <p className="text-xs text-red-600 w-full">{draftError}</p>}
        </div>
      )}

      {editingBoundaryZone && (
        <ZoneBoundaryEditorModal
          zone={editingBoundaryZone}
          initialCenter={zoneCentroid(editingBoundaryZone.id, devices)}
          devices={devices}
          onClose={() => setEditingBoundaryZone(null)}
        />
      )}
    </CollapsibleSection>
  )
}

// ─── Pristupne tačke grid — reuses the zone-management pattern (flat table,
// inline edits, add-row, bulk delete), not the device-management one: per
// the design doc, an access point is "an object, or a zone, just smaller" ──

type ApDraft = { label: string; access_point_type: string; description: string; zone_id: string }

function AccessPointGrid() {
  const qc = useQueryClient()
  const { data: accessPoints = [], isError, error } = useQuery({ queryKey: ['scada-access-points'], queryFn: () => scadaApi.listAccessPoints().then((r) => r.data) })
  const { data: zones = [] }     = useQuery({ queryKey: ['scada-zones'],      queryFn: () => scadaApi.listZones().then((r) => r.data), staleTime: 60_000 })
  const { data: devices = [] }   = useQuery({ queryKey: ['scada-devices'],    queryFn: () => scadaApi.listDevices().then((r) => r.data) })

  const [search, setSearch]         = useState('')
  const [selected, setSelected]     = useState<Set<string>>(new Set())
  const [draft, setDraft]           = useState<ApDraft | null>(null)
  const [draftError, setDraftError] = useState('')

  const zoneSelectOptions = zoneOptions(zones, null)
  const deviceCount = (apId: string) => devices.filter((d) => d.access_point_id === apId).length

  const visible = accessPoints
    .filter((ap) =>
      !search.trim() ||
      ap.label.toLowerCase().includes(search.toLowerCase()) ||
      ap.access_point_key.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => a.label.localeCompare(b.label))

  const patch = (id: string, body: Partial<Omit<ScadaAccessPoint, 'id' | 'access_point_key' | 'created_at'>>) => {
    scadaApi.updateAccessPoint(id, body)
      .then(() => qc.invalidateQueries({ queryKey: ['scada-access-points'] }))
      .catch((e: unknown) => alert(apiError(e, 'Greška pri čuvanju izmene.')))
  }
  const createMut = useMutation({
    mutationFn: (d: ApDraft) => scadaApi.createAccessPoint({
      label:             d.label.trim(),
      access_point_type: d.access_point_type.trim() || 'manhole',
      description:       d.description.trim() || null,
      zone_id:           d.zone_id,
      location_lat:      null,
      location_lng:      null,
      active:            true,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['scada-access-points'] }); setDraft(null); setDraftError('') },
    onError: () => setDraftError('Greška pri čuvanju'),
  })
  // 409 (devices still attached) surfaces as a blocking error per access
  // point, same as device delete already does — never a "delete anyway".
  const bulkDeleteMut = useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(ids.map((id) => scadaApi.deleteAccessPoint(id)))
      return results
        .map((r, i) => ({ r, id: ids[i] }))
        .filter((x): x is { r: PromiseRejectedResult; id: string } => x.r.status === 'rejected')
    },
    onSuccess: (failures) => {
      qc.invalidateQueries({ queryKey: ['scada-access-points'] })
      setSelected(new Set())
      if (failures.length > 0) {
        const lines = failures.map(({ r, id }) =>
          `${accessPoints.find((ap) => ap.id === id)?.label ?? id}: ${apiError(r.reason, 'Greška pri brisanju')}`)
        alert(`Neke pristupne tačke nisu obrisane:\n${lines.join('\n')}`)
      }
    },
  })
  const toggleSel = (id: string) => setSelected((s) => {
    const n = new Set(s)
    if (n.has(id)) n.delete(id); else n.add(id)
    return n
  })

  return (
    <CollapsibleSection
      title="Pristupne tačke"
      subtitle="Šahtovi i druge tačke pristupa na mreži"
      controls={<SearchInput value={search} onChange={setSearch} placeholder="Naziv ili ključ…" />}
    >
      {isError && (
        <p className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          Lista pristupnih tačaka nije mogla da se učita — {apiError(error, 'nepoznata greška')}.
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={TH}><span className="sr-only">Izaberi</span></th>
              <th className={TH}>Ključ</th>
              <th className={TH}>Naziv</th>
              <th className={TH}>Tip</th>
              <th className={TH}>Zona</th>
              <th className={TH}>Latitude</th>
              <th className={TH}>Longitude</th>
              <th className={TH}>Uređaji</th>
              <th className={TH}>Aktivna</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((ap) => (
              <tr key={ap.id} className={`border-b border-gray-50 hover:bg-gray-50/60 ${!ap.active ? 'opacity-50' : ''}`}>
                <td className={TD}>
                  <input type="checkbox" checked={selected.has(ap.id)} onChange={() => toggleSel(ap.id)} className="w-4 h-4 rounded-sm border-gray-300" />
                </td>
                <td className={`${TD} font-mono text-xs text-gray-500`}>{ap.access_point_key}</td>
                <td className={TD}><TextCell value={ap.label} onCommit={(v) => v && patch(ap.id, { label: v })} /></td>
                <td className={TD}><TextCell value={ap.access_point_type} onCommit={(v) => v && patch(ap.id, { access_point_type: v })} /></td>
                <td className={TD}><SelectCell value={ap.zone_id ?? ''} onCommit={(v) => patch(ap.id, { zone_id: v })} options={zoneSelectOptions} /></td>
                <td className={TD}>
                  <NumberCell value={ap.location_lat?.toString() ?? ''} placeholder="—" className={`${CELL_INPUT} w-24`}
                    onCommit={(v) => patch(ap.id, { location_lat: v === '' ? null : parseFloat(v) })} />
                </td>
                <td className={TD}>
                  <NumberCell value={ap.location_lng?.toString() ?? ''} placeholder="—" className={`${CELL_INPUT} w-24`}
                    onCommit={(v) => patch(ap.id, { location_lng: v === '' ? null : parseFloat(v) })} />
                </td>
                <td className={`${TD} text-sm text-gray-500 text-center`}>{deviceCount(ap.id)}</td>
                <td className={TD}><ToggleCell checked={ap.active} onCommit={(v) => patch(ap.id, { active: v })} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {visible.length === 0 && <p className="text-sm text-gray-400 py-4 text-center">Nema pristupnih tačaka.</p>}
      </div>

      <div className="flex items-center gap-3 mt-3">
        {draft === null && (
          <button onClick={() => setDraft({ label: '', access_point_type: 'manhole', description: '', zone_id: '' })}
            className="text-sm font-medium text-brand-cyan-text hover:text-brand-midnight">+ Dodaj red</button>
        )}
        {selected.size > 0 && (
          <button onClick={() => { if (confirm(`Obrisati ${selected.size} pristupnih tačaka?`)) bulkDeleteMut.mutate([...selected]) }}
            className="text-sm font-medium text-red-600 hover:text-red-700 ml-auto">
            Ukloni izabrano ({selected.size})
          </button>
        )}
      </div>

      {draft !== null && (
        <div className="mt-3 border border-blue-200 bg-blue-50/50 rounded-lg p-3 flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Naziv *</label>
            <input className={`${FORM_INPUT} w-52`} value={draft.label}
              onChange={(e) => setDraft((f) => f && { ...f, label: e.target.value })} placeholder="npr. Šaht kod škole" autoFocus />
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Tip</label>
            <input className={`${FORM_INPUT} w-32`} value={draft.access_point_type}
              onChange={(e) => setDraft((f) => f && { ...f, access_point_type: e.target.value })} placeholder="manhole" />
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Opis</label>
            <input className={`${FORM_INPUT} w-52`} value={draft.description}
              onChange={(e) => setDraft((f) => f && { ...f, description: e.target.value })} placeholder="Kratki opis" />
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Zona *</label>
            <select className={`${FORM_INPUT} w-44`} value={draft.zone_id}
              onChange={(e) => setDraft((f) => f && { ...f, zone_id: e.target.value })}>
              <option value="" disabled>— Izaberite zonu —</option>
              {zoneOptions(zones, null).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="flex gap-1.5">
            <button onClick={() => draft.label.trim() && draft.zone_id && createMut.mutate(draft)}
              disabled={createMut.isPending || !draft.label.trim() || !draft.zone_id}
              className="w-8 h-8 rounded-lg bg-brand-cyan hover:bg-brand-cyan-dark disabled:opacity-40 text-brand-midnight flex items-center justify-center">✓</button>
            <button onClick={() => { setDraft(null); setDraftError('') }}
              className="w-8 h-8 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-100 flex items-center justify-center">✕</button>
          </div>
          {draftError && <p className="text-xs text-red-600 w-full">{draftError}</p>}
        </div>
      )}
    </CollapsibleSection>
  )
}

// ─── Cevovodi (zona-veze) grid ─────────────────────────────────────────────────

type LinkDraft = {
  label: string
  from_zone_id: string
  to_zone_id: string
  nominal_flow_lps: string
  alert_loss_lps: string
  alert_loss_pct: string
}

// Shared by the folded-row quick-attach dropdowns and the expanded panel's
// search-and-add sub-panels below -- both just need "append one point at
// the pipe's computed default position," never a location the caller
// already knows (see pipeMidpoint above). Also used by the legacy
// flow_meter_node_id backfill, so the two paths can't drift apart.
function buildAttachPoint(pointType: 'device' | 'access_point', id: string, mid: { lat: number; lng: number; order: number }): ScadaZoneLinkPoint {
  return {
    lat: mid.lat, lng: mid.lng, recorded_at: new Date().toISOString(), order: mid.order,
    point_type: pointType,
    device_id: pointType === 'device' ? id : null,
    access_point_id: pointType === 'access_point' ? id : null,
  }
}

// The common-case convenience next to "Merač protoka"/"Senzor pritiska" in
// the folded row: attach IS a real path point underneath (buildAttachPoint,
// placed via pipeMidpoint), but for a pipe with just one sensor of a type
// it's a single pick, not a trip through "Uredi mapu". Search/multi-sensor
// management lives in the expanded panel's AttachedItemsSection instead --
// this stays a single dropdown, one candidate list, one attached item.
function QuickAttachDropdown({ label, attached, candidates, disabledReason, onAttach, onExpand }: {
  label: string
  attached: { id: string; label: string }[]
  candidates: { id: string; label: string }[]
  disabledReason: string | null
  onAttach: (id: string) => void
  onExpand: () => void
}) {
  if (attached.length >= 2) {
    return (
      <button onClick={onExpand} title={`Više od jednog — otvorite red za upravljanje`}>
        <Badge tone="info">{attached.length}× {label.toLowerCase()}</Badge>
      </button>
    )
  }
  if (attached.length === 1) {
    return <span className="text-sm text-gray-700 truncate block max-w-40" title={attached[0].label}>{attached[0].label}</span>
  }
  return (
    <select
      className={CELL_SELECT}
      value=""
      disabled={!!disabledReason}
      title={disabledReason ?? undefined}
      onChange={(e) => { if (e.target.value) onAttach(e.target.value) }}
    >
      <option value="">— Priključi —</option>
      {candidates.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
    </select>
  )
}

// One shared component for all three expanded-panel sections (Merači
// protoka / Senzori pritiska / Pristupne tačke) -- they differ only in
// which candidate pool and whether a reading applies, not in shape, so one
// parametrized component stays in sync instead of three drifting copies.
// Search-and-add mirrors RouteDetailPanel's attach flow (RoutesPage.tsx)
// almost verbatim: a debounced-free client-side filter (these lists are
// small, no server search needed here), a scrollable clickable-row list,
// commit/cancel. Attach/remove here only ever touches the path point --
// never creates, edits, or deletes the underlying device/access point.
function AttachedItemsSection({
  title, attached, candidates, showReadings, disabledReason, onAttach, onDetach,
}: {
  title: string
  attached: { id: string; label: string; reading: string | null }[]
  candidates: { id: string; label: string }[]
  showReadings: boolean
  disabledReason: string | null
  onAttach: (id: string) => void
  onDetach: (id: string) => void
}) {
  const hasItems = attached.length > 0
  const [manualOpen, setManualOpen] = useState<boolean | null>(null)
  const open = manualOpen ?? hasItems
  const [adding, setAdding] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState('')

  const visibleCandidates = candidates.filter((c) =>
    !search.trim() || c.label.toLowerCase().includes(search.toLowerCase()))

  return (
    <div className="border-t border-gray-100 pt-3">
      <button onClick={() => setManualOpen(!open)} className="w-full flex items-center justify-between gap-2 text-left">
        <div className="flex items-center gap-2">
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{title}</p>
          {hasItems && <Badge tone="neutral">{attached.length}</Badge>}
        </div>
        <ChevronIcon open={open} />
      </button>

      {open && (
        <div className="mt-2 space-y-2">
          {attached.length === 0 && <p className="text-xs text-gray-400">Ništa priključeno.</p>}
          {attached.map((it) => (
            <div key={it.id} className="flex items-center justify-between gap-2 text-sm bg-gray-50 rounded-lg px-3 py-1.5">
              <span className="text-gray-800 truncate">{it.label}</span>
              <div className="flex items-center gap-2 shrink-0">
                {showReadings && <span className="text-xs text-gray-400">{it.reading ?? 'Nema podataka'}</span>}
                <button
                  onClick={() => onDetach(it.id)}
                  title="Uklanja samo sa ove putanje, ne briše uređaj"
                  className="text-gray-400 hover:text-red-500 text-xs"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}

          {adding ? (
            <div className="space-y-1.5">
              <input
                className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 outline-hidden"
                placeholder="Pretraga…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setSelectedId('') }}
                autoFocus
              />
              {visibleCandidates.length > 0 ? (
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-40 overflow-y-auto bg-white">
                  {visibleCandidates.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setSelectedId(c.id)}
                      className={`w-full text-left px-2.5 py-1.5 text-xs hover:bg-gray-50 transition-colors ${selectedId === c.id ? 'bg-blue-50 border-l-2 border-blue-500' : ''}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 text-center py-1.5">Nema rezultata</p>
              )}
              {disabledReason && <p className="text-[11px] text-amber-600">{disabledReason}</p>}
              <div className="flex gap-1.5">
                <button onClick={() => { setAdding(false); setSearch(''); setSelectedId('') }}
                  className="flex-1 py-1.5 text-xs text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100">
                  Otkaži
                </button>
                <button
                  onClick={() => { if (selectedId) { onAttach(selectedId); setAdding(false); setSearch(''); setSelectedId('') } }}
                  disabled={!selectedId || !!disabledReason}
                  className="flex-1 py-1.5 text-xs font-medium text-brand-midnight bg-brand-cyan hover:bg-brand-cyan-dark disabled:opacity-50 rounded-lg"
                >
                  Dodaj
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => setAdding(true)} disabled={!!disabledReason} title={disabledReason ?? undefined}
              className="text-xs font-medium text-brand-cyan-text hover:text-brand-midnight disabled:opacity-40 disabled:cursor-not-allowed">
              + Dodaj
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// Section 0 (always visible) + the three foldable attach sections. A device
// (flow meter / pressure sensor) attached via a path point of point_type
// 'device' whose node_type matches; access points via point_type
// 'access_point' -- derived from link.path per docs/decision-device-
// ownership-and-attachment.md, never from the legacy flow_meter_node_id.
function PipelineDetailPanel({
  link, devices, accessPoints, disabledReason, onPatch, onAttach, onDetach,
}: {
  link: ScadaZoneLink
  devices: ScadaDevice[]
  accessPoints: ScadaAccessPoint[]
  disabledReason: string | null
  onPatch: (body: Partial<Omit<ScadaZoneLink, 'id' | 'created_at'>>) => void
  onAttach: (pointType: 'device' | 'access_point', id: string) => void
  onDetach: (point: ScadaZoneLinkPoint) => void
}) {
  const deviceById = new Map(devices.map((d) => [d.id, d]))
  const accessPointById = new Map(accessPoints.map((ap) => [ap.id, ap]))

  const devicePointsByNodeType = (nodeType: 'flow_meter' | 'pressure_sensor') =>
    link.path
      .filter((p) => p.point_type === 'device' && p.device_id && deviceById.get(p.device_id)?.node_type === nodeType)
      .map((p) => ({ point: p, device: deviceById.get(p.device_id!)! }))

  const flowPoints = devicePointsByNodeType('flow_meter')
  const pressurePoints = devicePointsByNodeType('pressure_sensor')
  const apPoints = link.path
    .filter((p) => p.point_type === 'access_point' && p.access_point_id && accessPointById.has(p.access_point_id))
    .map((p) => ({ point: p, ap: accessPointById.get(p.access_point_id!)! }))

  const readingOf = (d: ScadaDevice) => d.latest_value != null ? `${d.latest_value.toFixed(2)}${d.measurement_unit ? ` ${d.measurement_unit}` : ''}` : null
  const avg = (vals: number[]) => vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null
  const avgFlow = avg(flowPoints.map(({ device }) => device.latest_value).filter((v): v is number => v != null))
  const avgPressure = avg(pressurePoints.map(({ device }) => device.latest_value).filter((v): v is number => v != null))
  // Flow meters can be assumed L/s (that's what latest_value means for that
  // node_type, per api/scada.ts's own field comment); pressure sensors have
  // no such fixed unit, so the first attached one's measurement_unit stands
  // in -- a real per-device mix would need per-device tiles, not one average.
  const pressureUnit = pressurePoints[0]?.device.measurement_unit ?? ''

  const attachedDeviceIds = new Set([...flowPoints, ...pressurePoints].map(({ device }) => device.id))
  const attachedApIds = new Set(apPoints.map(({ ap }) => ap.id))

  return (
    <div className="space-y-4">
      <div>
        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Podešavanja i očitavanja</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Stat label="Prosečan protok" value={avgFlow != null ? `${avgFlow.toFixed(2)} L/s` : '—'} />
          <Stat label="Prosečan pritisak" value={avgPressure != null ? `${avgPressure.toFixed(2)}${pressureUnit ? ` ${pressureUnit}` : ''}` : '—'} />
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Nominalni protok (L/s)</label>
            <NumberCell className={FORM_INPUT} value={link.nominal_flow_lps?.toString() ?? ''}
              onCommit={(v) => onPatch({ nominal_flow_lps: v === '' ? null : parseFloat(v) })} />
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Alarm gubitka (L/s)</label>
            <NumberCell className={FORM_INPUT} value={link.alert_loss_lps?.toString() ?? ''}
              onCommit={(v) => onPatch({ alert_loss_lps: v === '' ? null : parseFloat(v) })} />
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Alarm gubitka (%)</label>
            <NumberCell className={FORM_INPUT} value={link.alert_loss_pct?.toString() ?? ''}
              onCommit={(v) => onPatch({ alert_loss_pct: v === '' ? null : parseFloat(v) })} />
          </div>
        </div>
      </div>

      <AttachedItemsSection
        title="Merači protoka"
        attached={flowPoints.map(({ device }) => ({ id: device.id, label: device.label, reading: readingOf(device) }))}
        candidates={devices.filter((d) => d.node_type === 'flow_meter' && d.active && !attachedDeviceIds.has(d.id)).map((d) => ({ id: d.id, label: d.label }))}
        showReadings
        disabledReason={disabledReason}
        onAttach={(id) => onAttach('device', id)}
        onDetach={(id) => { const found = flowPoints.find(({ device }) => device.id === id); if (found) onDetach(found.point) }}
      />
      <AttachedItemsSection
        title="Senzori pritiska"
        attached={pressurePoints.map(({ device }) => ({ id: device.id, label: device.label, reading: readingOf(device) }))}
        candidates={devices.filter((d) => d.node_type === 'pressure_sensor' && d.active && !attachedDeviceIds.has(d.id)).map((d) => ({ id: d.id, label: d.label }))}
        showReadings
        disabledReason={disabledReason}
        onAttach={(id) => onAttach('device', id)}
        onDetach={(id) => { const found = pressurePoints.find(({ device }) => device.id === id); if (found) onDetach(found.point) }}
      />
      <AttachedItemsSection
        title="Pristupne tačke"
        attached={apPoints.map(({ ap }) => ({ id: ap.id, label: ap.label, reading: null }))}
        candidates={accessPoints.filter((ap) => ap.active && !attachedApIds.has(ap.id)).map((ap) => ({ id: ap.id, label: ap.label }))}
        showReadings={false}
        disabledReason={disabledReason}
        onAttach={(id) => onAttach('access_point', id)}
        onDetach={(id) => { const found = apPoints.find(({ ap }) => ap.id === id); if (found) onDetach(found.point) }}
      />
    </div>
  )
}

function CevovodiGrid() {
  const qc = useQueryClient()
  const { user } = useAuthStore()
  const canEditPath = user?.roles.some((r) => ['admin', 'company_admin', 'scada_operator'].includes(r)) ?? false
  const { data: zoneLinks = [] }     = useQuery({ queryKey: ['scada-zone-links'],     queryFn: () => scadaApi.listZoneLinks().then((r) => r.data), staleTime: 60_000 })
  const { data: zones = [] }         = useQuery({ queryKey: ['scada-zones'],          queryFn: () => scadaApi.listZones().then((r) => r.data), staleTime: 60_000 })
  const { data: devices = [] }       = useQuery({ queryKey: ['scada-devices'],        queryFn: () => scadaApi.listDevices().then((r) => r.data) })
  const { data: accessPoints = [] }  = useQuery({ queryKey: ['scada-access-points'],  queryFn: () => scadaApi.listAccessPoints().then((r) => r.data), staleTime: 60_000 })

  const [search, setSearch]         = useState('')
  const [zoneFilter, setZoneFilter] = useState('')
  const [selected, setSelected]     = useState<Set<string>>(new Set())
  const [draft, setDraft]           = useState<LinkDraft | null>(null)
  const [draftError, setDraftError] = useState('')
  const [editingLink, setEditingLink] = useState<ScadaZoneLink | null>(null)
  const [expanded, setExpanded]     = useState<string | null>(null)

  const visible = zoneLinks.filter((lk) =>
    (!search.trim() || lk.label.toLowerCase().includes(search.toLowerCase())) &&
    (!zoneFilter || lk.from_zone_id === zoneFilter || lk.to_zone_id === zoneFilter)
  )
  const zoneSelectOptions = zoneOptions(zones, '— Spoljni —')
  const deviceById = new Map(devices.map((d) => [d.id, d]))

  const disabledReasonFor = (lk: ScadaZoneLink): string | null =>
    pipeMidpoint(lk, zones, devices) === null
      ? 'Cevovod nema definisane zone ni putanju — koristite „Uredi mapu" za ručno postavljanje.'
      : null

  const patch = (id: string, body: Partial<Omit<ScadaZoneLink, 'id' | 'created_at'>>) => {
    scadaApi.updateZoneLink(id, body)
      .then(() => qc.invalidateQueries({ queryKey: ['scada-zone-links'] }))
      .catch((e: unknown) => alert(apiError(e, 'Greška pri čuvanju izmene.')))
  }
  const createMut = useMutation({
    mutationFn: (d: LinkDraft) => scadaApi.createZoneLink({
      label:               d.label.trim(),
      from_zone_id:        d.from_zone_id || null,
      to_zone_id:          d.to_zone_id || null,
      flow_meter_node_id:  null,
      nominal_flow_lps:    d.nominal_flow_lps ? parseFloat(d.nominal_flow_lps) : null,
      alert_loss_lps:      d.alert_loss_lps ? parseFloat(d.alert_loss_lps) : null,
      alert_loss_pct:      d.alert_loss_pct ? parseFloat(d.alert_loss_pct) : null,
      path:                [],
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['scada-zone-links'] }); setDraft(null); setDraftError('') },
    onError: () => setDraftError('Greška pri čuvanju'),
  })
  const bulkDeleteMut = useMutation({
    mutationFn: async (ids: string[]) => { await Promise.all(ids.map((id) => scadaApi.deleteZoneLink(id))) },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['scada-zone-links'] }); setSelected(new Set()) },
    onError: (e: unknown) => alert(apiError(e, 'Greška pri brisanju.')),
  })
  const toggleSel = (id: string) => setSelected((s) => {
    const n = new Set(s)
    if (n.has(id)) n.delete(id); else n.add(id)
    return n
  })

  // Shared by the folded quick-attach dropdowns and the expanded panel's
  // search-and-add -- appends one real path point at the pipe's computed
  // midpoint (pipeMidpoint above). Physical placement stays "Uredi mapu"'s
  // job, done later, separately, by whoever has the location.
  const attachMut = useMutation({
    mutationFn: ({ link, pointType, id }: { link: ScadaZoneLink; pointType: 'device' | 'access_point'; id: string }) => {
      const mid = pipeMidpoint(link, zones, devices)
      if (!mid) return Promise.reject(new Error('Cevovod nema definisane zone ni putanju.'))
      return scadaApi.updateZoneLinkPath(link.id, [...link.path, buildAttachPoint(pointType, id, mid)])
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scada-zone-links'] }),
    onError: (e: unknown) => alert(apiError(e, 'Greška pri dodavanju.')),
  })
  // Removes only the path point -- never the device/access point itself.
  const detachMut = useMutation({
    mutationFn: ({ link, point }: { link: ScadaZoneLink; point: ScadaZoneLinkPoint }) =>
      scadaApi.updateZoneLinkPath(link.id, link.path.filter((p) => p !== point)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scada-zone-links'] }),
    onError: (e: unknown) => alert(apiError(e, 'Greška pri uklanjanju.')),
  })

  // Legacy flow_meter_node_id -> a real path point, one-time per pipe. Not
  // touching the field itself (the backend's zone-balance calc still reads
  // it) -- additive only. Self-hides once every qualifying pipe is done;
  // re-running only retries whatever's still unmigrated, since `qualifying`
  // is recomputed from live (post-invalidation) data on every render.
  const qualifyingLegacy = zoneLinks.filter((lk) =>
    lk.flow_meter_node_id && !lk.path.some((p) => p.point_type === 'device' && p.device_id === lk.flow_meter_node_id))
  const backfillMut = useMutation({
    mutationFn: async () => {
      const results = await Promise.allSettled(qualifyingLegacy.map((lk) => {
        const mid = pipeMidpoint(lk, zones, devices)
        if (!mid) return Promise.reject(new Error('nema definisane zone ni putanju'))
        return scadaApi.updateZoneLinkPath(lk.id, [...lk.path, buildAttachPoint('device', lk.flow_meter_node_id!, mid)])
      }))
      return results
        .map((r, i) => ({ r, lk: qualifyingLegacy[i] }))
        .filter((x): x is { r: PromiseRejectedResult; lk: ScadaZoneLink } => x.r.status === 'rejected')
    },
    onSuccess: (failures) => {
      qc.invalidateQueries({ queryKey: ['scada-zone-links'] })
      if (failures.length > 0) {
        const lines = failures.map(({ r, lk }) => `${lk.label}: ${apiError(r.reason, 'Greška')}`)
        alert(`Neki cevovodi nisu uvezeni:\n${lines.join('\n')}`)
      }
    },
  })

  return (
    <CollapsibleSection
      title="Cevovodi (zona-veze)"
      subtitle="Kauzalnost toka — koji rezervoar snabdeva koji"
      controls={<>
        {canEditPath && qualifyingLegacy.length > 0 && (
          <button
            onClick={() => {
              if (confirm(`Uvesti ${qualifyingLegacy.length} stari(h) merača protoka kao tačke na putanji? Ovo neće ukloniti stara podešavanja, samo ih dopuniti.`)) {
                backfillMut.mutate()
              }
            }}
            disabled={backfillMut.isPending}
            className="px-2.5 py-1.5 text-xs font-medium text-brand-cyan-text hover:text-brand-midnight border border-blue-200 rounded-lg disabled:opacity-50"
          >
            Uvezi {qualifyingLegacy.length} stari(h) merača protoka u putanju
          </button>
        )}
        <select className="px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg" value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)}>
          <option value="">Sve zone</option>
          {zones.filter((z) => z.active).map((z) => <option key={z.id} value={z.id}>{z.label}</option>)}
        </select>
        <SearchInput value={search} onChange={setSearch} placeholder="Naziv cevovoda…" />
      </>}
    >
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={TH}></th>
              <th className={TH}></th>
              <th className={TH}>Naziv</th>
              <th className={TH}>Merač protoka</th>
              <th className={TH}>Senzor pritiska</th>
              <th className={TH}>Iz zone</th>
              <th className={TH}>U zonu</th>
              <th className={TH}>Aktivan</th>
              <th className={TH}></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((lk) => {
              const disabledReason = disabledReasonFor(lk)
              const flowAttached = lk.path
                .filter((p) => p.point_type === 'device' && p.device_id && deviceById.get(p.device_id)?.node_type === 'flow_meter')
                .map((p) => deviceById.get(p.device_id!)).filter((d): d is ScadaDevice => d != null)
              const pressureAttached = lk.path
                .filter((p) => p.point_type === 'device' && p.device_id && deviceById.get(p.device_id)?.node_type === 'pressure_sensor')
                .map((p) => deviceById.get(p.device_id!)).filter((d): d is ScadaDevice => d != null)
              const flowCandidates = devices.filter((d) => d.node_type === 'flow_meter' && d.active && !flowAttached.some((a) => a.id === d.id))
              const pressureCandidates = devices.filter((d) => d.node_type === 'pressure_sensor' && d.active && !pressureAttached.some((a) => a.id === d.id))

              return (
                <Fragment key={lk.id}>
                  <tr className={`border-b border-gray-50 hover:bg-gray-50/60 ${!lk.active ? 'opacity-50' : ''}`}>
                    <td className={TD}><input type="checkbox" checked={selected.has(lk.id)} onChange={() => toggleSel(lk.id)} className="w-4 h-4 rounded-sm border-gray-300" /></td>
                    <td className={TD}>
                      <button onClick={() => setExpanded(expanded === lk.id ? null : lk.id)} className="text-gray-400 hover:text-gray-600" title="Podešavanja i priključeni uređaji">
                        <ChevronIcon open={expanded === lk.id} />
                      </button>
                    </td>
                    <td className={TD}><TextCell value={lk.label} onCommit={(v) => v && patch(lk.id, { label: v })} /></td>
                    <td className={TD}>
                      <QuickAttachDropdown
                        label="Merač protoka"
                        attached={flowAttached.map((d) => ({ id: d.id, label: d.label }))}
                        candidates={flowCandidates.map((d) => ({ id: d.id, label: d.label }))}
                        disabledReason={disabledReason}
                        onAttach={(id) => attachMut.mutate({ link: lk, pointType: 'device', id })}
                        onExpand={() => setExpanded(lk.id)}
                      />
                    </td>
                    <td className={TD}>
                      <QuickAttachDropdown
                        label="Senzor pritiska"
                        attached={pressureAttached.map((d) => ({ id: d.id, label: d.label }))}
                        candidates={pressureCandidates.map((d) => ({ id: d.id, label: d.label }))}
                        disabledReason={disabledReason}
                        onAttach={(id) => attachMut.mutate({ link: lk, pointType: 'device', id })}
                        onExpand={() => setExpanded(lk.id)}
                      />
                    </td>
                    <td className={TD}><SelectCell value={lk.from_zone_id ?? ''} onCommit={(v) => patch(lk.id, { from_zone_id: v || null })} options={zoneSelectOptions} /></td>
                    <td className={TD}><SelectCell value={lk.to_zone_id ?? ''} onCommit={(v) => patch(lk.id, { to_zone_id: v || null })} options={zoneSelectOptions} /></td>
                    <td className={TD}><ToggleCell checked={lk.active} onCommit={(v) => patch(lk.id, { active: v })} /></td>
                    <td className={TD}>
                      {canEditPath && (
                        <button onClick={() => setEditingLink(lk)} className="text-xs font-medium text-brand-poi-text hover:text-brand-midnight whitespace-nowrap">
                          Uredi mapu
                        </button>
                      )}
                    </td>
                  </tr>
                  {expanded === lk.id && (
                    <tr className="bg-gray-50/60 border-b border-gray-100">
                      <td colSpan={9} className="px-4 py-4">
                        <PipelineDetailPanel
                          link={lk}
                          devices={devices}
                          accessPoints={accessPoints}
                          disabledReason={disabledReason}
                          onPatch={(body) => patch(lk.id, body)}
                          onAttach={(pointType, id) => attachMut.mutate({ link: lk, pointType, id })}
                          onDetach={(point) => detachMut.mutate({ link: lk, point })}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
        {visible.length === 0 && <p className="text-sm text-gray-400 py-4 text-center">Nema definisanih cevovoda.</p>}
      </div>

      <div className="flex items-center gap-3 mt-3">
        {draft === null && (
          <button onClick={() => setDraft({ label: '', from_zone_id: '', to_zone_id: '', nominal_flow_lps: '', alert_loss_lps: '', alert_loss_pct: '' })}
            className="text-sm font-medium text-brand-cyan-text hover:text-brand-midnight">+ Dodaj red</button>
        )}
        {selected.size > 0 && (
          <button onClick={() => { if (confirm(`Obrisati ${selected.size} cevovoda?`)) bulkDeleteMut.mutate([...selected]) }}
            className="text-sm font-medium text-red-600 hover:text-red-700 ml-auto">Ukloni izabrano ({selected.size})</button>
        )}
      </div>

      {draft !== null && (
        <div className="mt-3 border border-blue-200 bg-blue-50/50 rounded-lg p-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="col-span-2">
            <label className="block text-[11px] text-gray-500 mb-1">Naziv *</label>
            <input className={FORM_INPUT} value={draft.label}
              onChange={(e) => setDraft((f) => f && { ...f, label: e.target.value })} placeholder="npr. Cevovod Razbojište → Lubenin" autoFocus />
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Iz zone</label>
            <select className={FORM_INPUT} value={draft.from_zone_id}
              onChange={(e) => setDraft((f) => f && { ...f, from_zone_id: e.target.value })}>
              {zoneSelectOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">U zonu</label>
            <select className={FORM_INPUT} value={draft.to_zone_id}
              onChange={(e) => setDraft((f) => f && { ...f, to_zone_id: e.target.value })}>
              {zoneSelectOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Nominalni protok (L/s)</label>
            <input type="number" step="any" className={FORM_INPUT} value={draft.nominal_flow_lps}
              onChange={(e) => setDraft((f) => f && { ...f, nominal_flow_lps: e.target.value })} placeholder="18.0" />
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Alarm gubitka (L/s)</label>
            <input type="number" step="any" className={FORM_INPUT} value={draft.alert_loss_lps}
              onChange={(e) => setDraft((f) => f && { ...f, alert_loss_lps: e.target.value })} placeholder="3.0" />
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Alarm gubitka (%)</label>
            <input type="number" step="any" className={FORM_INPUT} value={draft.alert_loss_pct}
              onChange={(e) => setDraft((f) => f && { ...f, alert_loss_pct: e.target.value })} placeholder="15" />
          </div>
          <div className="flex gap-1.5 items-end">
            <button onClick={() => draft.label.trim() && createMut.mutate(draft)} disabled={createMut.isPending || !draft.label.trim()}
              className="w-8 h-8 rounded-lg bg-brand-cyan hover:bg-brand-cyan-dark disabled:opacity-40 text-brand-midnight flex items-center justify-center">✓</button>
            <button onClick={() => { setDraft(null); setDraftError('') }}
              className="w-8 h-8 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-100 flex items-center justify-center">✕</button>
          </div>
          {draftError && <p className="text-xs text-red-600 col-span-full">{draftError}</p>}
        </div>
      )}

      {editingLink && <PipelinePathEditorModal link={editingLink} onClose={() => setEditingLink(null)} />}
    </CollapsibleSection>
  )
}

// ─── Nadzor plovka (pumpa–rezervoar) grid — monitoring only, no remote control ──

type PrlDraft = {
  pump_node_id: string
  reservoir_node_id: string
  activation_depth_m: string
  shutoff_depth_m: string
  startup_delay_minutes: string
}

function NadzorPlovkaGrid() {
  const qc = useQueryClient()
  const { data: pumpLinks = [] } = useQuery({ queryKey: ['scada-pump-reservoir-links'], queryFn: () => scadaApi.listPumpReservoirLinks().then((r) => r.data) })
  const { data: devices = [] }   = useQuery({ queryKey: ['scada-devices'], queryFn: () => scadaApi.listDevices().then((r) => r.data) })

  const pumps      = devices.filter((d) => d.node_type === 'pump')
  const reservoirs = devices.filter((d) => d.node_type === 'reservoir')
  const deviceById = (id: string) => devices.find((d) => d.id === id)

  const [selected, setSelected]     = useState<Set<string>>(new Set())
  const [draft, setDraft]           = useState<PrlDraft | null>(null)
  const [draftError, setDraftError] = useState('')

  const patch = (id: string, body: Partial<Pick<PumpReservoirLink, 'activation_depth_m' | 'shutoff_depth_m' | 'startup_delay_minutes' | 'active'>>) => {
    scadaApi.updatePumpReservoirLink(id, body)
      .then(() => qc.invalidateQueries({ queryKey: ['scada-pump-reservoir-links'] }))
      .catch((e: unknown) => alert(apiError(e, 'Greška pri čuvanju izmene.')))
  }
  const createMut = useMutation({
    mutationFn: (d: PrlDraft) => scadaApi.createPumpReservoirLink({
      pump_node_id:           d.pump_node_id,
      reservoir_node_id:      d.reservoir_node_id,
      activation_depth_m:     parseFloat(d.activation_depth_m),
      shutoff_depth_m:        parseFloat(d.shutoff_depth_m),
      startup_delay_minutes:  d.startup_delay_minutes ? parseInt(d.startup_delay_minutes, 10) : 5,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['scada-pump-reservoir-links'] }); setDraft(null); setDraftError('') },
    onError: (e: unknown) => setDraftError(apiError(e, 'Greška pri čuvanju')),
  })
  const bulkDeleteMut = useMutation({
    mutationFn: async (ids: string[]) => { await Promise.all(ids.map((id) => scadaApi.deletePumpReservoirLink(id))) },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['scada-pump-reservoir-links'] }); setSelected(new Set()) },
    onError: (e: unknown) => alert(apiError(e, 'Greška pri brisanju.')),
  })
  const toggleSel = (id: string) => setSelected((s) => {
    const n = new Set(s)
    if (n.has(id)) n.delete(id); else n.add(id)
    return n
  })

  const numPatch = (id: string, key: 'activation_depth_m' | 'shutoff_depth_m' | 'startup_delay_minutes') => (v: string) => {
    const n = key === 'startup_delay_minutes' ? parseInt(v, 10) : parseFloat(v)
    if (Number.isNaN(n)) { alert('Unesite ispravan broj.'); return }
    patch(id, { [key]: n })
  }

  const draftValid = !!draft && draft.pump_node_id && draft.reservoir_node_id && draft.activation_depth_m && draft.shutoff_depth_m

  return (
    <CollapsibleSection
      title="Nadzor plovka (pumpa–rezervoar)"
      subtitle="Samo očitavanje — koristi se za alarm „pumpa nije startovala“, bez daljinske kontrole pumpe"
    >
      <p className="text-xs text-gray-400 -mt-1 mb-3">
        Ovi pragovi opisuju očekivano ponašanje plovka radi upoređivanja sa stvarnim očitavanjima. Sistem ne šalje komande pumpi.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={TH}></th>
              <th className={TH}>Pumpa</th>
              <th className={TH}>Rezervoar</th>
              <th className={TH}>Očekivano uključenje ispod (m)</th>
              <th className={TH}>Očekivano isključenje iznad (m)</th>
              <th className={TH}>Tolerancija pre alarma (min)</th>
              <th className={TH}>Aktivan</th>
            </tr>
          </thead>
          <tbody>
            {pumpLinks.map((pl) => {
              const pump      = deviceById(pl.pump_node_id)
              const reservoir = deviceById(pl.reservoir_node_id)
              return (
                <tr key={pl.id} className={`border-b border-gray-50 hover:bg-gray-50/60 ${!pl.active ? 'opacity-50' : ''}`}>
                  <td className={TD}><input type="checkbox" checked={selected.has(pl.id)} onChange={() => toggleSel(pl.id)} className="w-4 h-4 rounded-sm border-gray-300" /></td>
                  <td className={`${TD} text-sm text-gray-700`}>{pump?.label ?? '—'}</td>
                  <td className={`${TD} text-sm text-gray-700`}>{reservoir?.label ?? '—'}</td>
                  <td className={TD}><NumberCell value={pl.activation_depth_m.toString()} onCommit={numPatch(pl.id, 'activation_depth_m')} /></td>
                  <td className={TD}><NumberCell value={pl.shutoff_depth_m.toString()} onCommit={numPatch(pl.id, 'shutoff_depth_m')} /></td>
                  <td className={TD}><NumberCell step="1" value={pl.startup_delay_minutes.toString()} onCommit={numPatch(pl.id, 'startup_delay_minutes')} /></td>
                  <td className={TD}><ToggleCell checked={pl.active} onCommit={(v) => patch(pl.id, { active: v })} /></td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {pumpLinks.length === 0 && <p className="text-sm text-gray-400 py-4 text-center">Nema definisanih veza pumpa–rezervoar.</p>}
      </div>

      <div className="flex items-center gap-3 mt-3">
        {draft === null && (
          <button onClick={() => setDraft({ pump_node_id: '', reservoir_node_id: '', activation_depth_m: '', shutoff_depth_m: '', startup_delay_minutes: '5' })}
            className="text-sm font-medium text-brand-cyan-text hover:text-brand-midnight">+ Dodaj red</button>
        )}
        {selected.size > 0 && (
          <button onClick={() => { if (confirm(`Obrisati ${selected.size} pravila nadzora?`)) bulkDeleteMut.mutate([...selected]) }}
            className="text-sm font-medium text-red-600 hover:text-red-700 ml-auto">Ukloni izabrano ({selected.size})</button>
        )}
      </div>

      {draft !== null && (
        <div className="mt-3 border border-blue-200 bg-blue-50/50 rounded-lg p-3 grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Pumpa *</label>
            <select className={FORM_INPUT} value={draft.pump_node_id}
              onChange={(e) => setDraft((f) => f && { ...f, pump_node_id: e.target.value })} autoFocus>
              <option value="">— Izaberite —</option>
              {pumps.map((d) => <option key={d.id} value={d.id}>{d.label} ({d.device_key})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Rezervoar *</label>
            <select className={FORM_INPUT} value={draft.reservoir_node_id}
              onChange={(e) => setDraft((f) => f && { ...f, reservoir_node_id: e.target.value })}>
              <option value="">— Izaberite —</option>
              {reservoirs.map((d) => <option key={d.id} value={d.id}>{d.label} ({d.device_key})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Očekivano uključenje ispod (m) *</label>
            <input type="number" step="any" className={FORM_INPUT} value={draft.activation_depth_m}
              onChange={(e) => setDraft((f) => f && { ...f, activation_depth_m: e.target.value })} placeholder="1.20" />
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Očekivano isključenje iznad (m) *</label>
            <input type="number" step="any" className={FORM_INPUT} value={draft.shutoff_depth_m}
              onChange={(e) => setDraft((f) => f && { ...f, shutoff_depth_m: e.target.value })} placeholder="2.80" />
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Tolerancija pre alarma (min)</label>
            <input type="number" step="1" min="0" className={FORM_INPUT} value={draft.startup_delay_minutes}
              onChange={(e) => setDraft((f) => f && { ...f, startup_delay_minutes: e.target.value })} placeholder="5" />
          </div>
          <div className="flex gap-1.5 items-end col-span-2 sm:col-span-1">
            <button onClick={() => draftValid && createMut.mutate(draft)} disabled={createMut.isPending || !draftValid}
              className="w-8 h-8 rounded-lg bg-brand-cyan hover:bg-brand-cyan-dark disabled:opacity-40 text-brand-midnight flex items-center justify-center">✓</button>
            <button onClick={() => { setDraft(null); setDraftError('') }}
              className="w-8 h-8 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-100 flex items-center justify-center">✕</button>
          </div>
          {draftError && <p className="text-xs text-red-600 col-span-full">{draftError}</p>}
        </div>
      )}
    </CollapsibleSection>
  )
}

// ─── Infrastrukturne zone (Meters-module widget — owned by /meters, gated) ─────

const ZONE_TYPES = ['reservoir_catchment', 'pipeline_branch', 'water_source'] as const

// ZoneForm must be defined outside ZonesSection — defining it inside causes React
// to treat it as a new component type on every render (new function reference),
// which unmounts/remounts the inputs and kills focus after every keystroke.
function ZoneForm({
  form,
  setForm,
  formError,
  onSave,
  onCancel,
  saving,
}: {
  form: { name: string; zone_type: string; description: string }
  setForm: React.Dispatch<React.SetStateAction<{ name: string; zone_type: string; description: string }>>
  formError: string
  onSave: () => void
  onCancel: () => void
  saving: boolean
}) {
  return (
    <div className="border border-blue-200 bg-blue-50 rounded-xl p-4 space-y-3 mt-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Naziv *</label>
          <input
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-hidden bg-white"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="npr. Zona A — Centar"
            autoFocus
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Tip</label>
          <select
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-hidden bg-white"
            value={form.zone_type}
            onChange={(e) => setForm((f) => ({ ...f, zone_type: e.target.value }))}
          >
            {ZONE_TYPES.map((t) => (
              <option key={t} value={t}>{ZONE_TYPE_LABEL[t]}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Opis (opciono)</label>
        <input
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-hidden bg-white"
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          placeholder="Kratki opis zone"
        />
      </div>
      {formError && <p className="text-xs text-red-600">{formError}</p>}
      <div className="flex gap-2">
        <button onClick={onSave} disabled={!form.name.trim() || saving}
          className="flex-1 bg-brand-cyan hover:bg-brand-cyan-dark disabled:opacity-50 text-brand-midnight text-sm font-medium py-2 rounded-lg">
          {saving ? 'Čuvanje…' : 'Sačuvaj'}
        </button>
        <button onClick={onCancel} className="flex-1 border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium py-2 rounded-lg bg-white">
          Otkaži
        </button>
      </div>
    </div>
  )
}

function ZonesSection() {
  const qc = useQueryClient()
  const { user } = useAuthStore()
  const isAdmin = user?.roles.some((r) => ['admin', 'company_admin'].includes(r)) ?? false

  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [showInactive, setShowInactive] = useState(false)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<Zone | null>(null)
  const [form, setForm] = useState({ name: '', zone_type: 'reservoir_catchment', description: '' })
  const [formError, setFormError] = useState('')

  const { data: zones = [] } = useQuery({
    queryKey: ['zones', typeFilter, showInactive],
    queryFn: () => zonesApi.list({
      zone_type: typeFilter === 'all' ? undefined : typeFilter,
      active_only: !showInactive,
    }).then((r) => r.data),
    staleTime: 30_000,
  })

  const createMut = useMutation({
    mutationFn: () => zonesApi.create({ name: form.name.trim(), zone_type: form.zone_type, description: form.description.trim() || undefined }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['zones'] }); setCreating(false); setForm({ name: '', zone_type: 'reservoir_catchment', description: '' }); setFormError('') },
    onError: () => setFormError('Greška pri čuvanju'),
  })

  const updateMut = useMutation({
    // null, not undefined -- PATCH now reads an omitted key as "leave
    // alone," so an emptied description has to actually say so to clear.
    mutationFn: (z: Zone) => zonesApi.update(z.id, { name: form.name.trim(), zone_type: form.zone_type, description: form.description.trim() || null }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['zones'] }); setEditing(null); setFormError('') },
    onError: () => setFormError('Greška pri čuvanju'),
  })

  const toggleActive = useMutation({
    mutationFn: (z: Zone) => zonesApi.update(z.id, { active: !z.active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['zones'] }),
  })

  function openEdit(z: Zone) {
    setEditing(z)
    setForm({ name: z.name, zone_type: z.zone_type, description: z.description ?? '' })
    setFormError('')
  }

  function openCreate() {
    setCreating(true)
    setEditing(null)
    setForm({ name: '', zone_type: 'reservoir_catchment', description: '' })
    setFormError('')
  }

  return (
    <div className="bg-gray-50 border-t border-gray-200">
      <div className="max-w-(--breakpoint-xl) mx-auto px-6 py-8 space-y-5">

        {/* Header row */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Infrastrukturne zone</h2>
            <p className="text-xs text-gray-500 mt-0.5">Slivna područja, grane cevovoda i vodozahvati</p>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer select-none">
              <input type="checkbox" className="rounded-sm" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
              Prikaži neaktivne
            </label>
            {isAdmin && !creating && (
              <button onClick={openCreate} className="flex items-center gap-1.5 bg-brand-cyan hover:bg-brand-cyan-dark text-brand-midnight text-sm font-medium px-3 py-2 rounded-lg">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                Nova zona
              </button>
            )}
          </div>
        </div>

        {/* Create form */}
        {creating && (
          <ZoneForm
            form={form}
            setForm={setForm}
            formError={formError}
            onSave={() => { if (!form.name.trim()) { setFormError('Naziv je obavezan'); return }; createMut.mutate() }}
            onCancel={() => setCreating(false)}
            saving={createMut.isPending}
          />
        )}

        {/* Type filter tabs */}
        <div className="flex bg-white border border-gray-200 rounded-lg p-1 gap-1 w-fit">
          {[{ key: 'all', label: 'Sve' }, ...ZONE_TYPES.map((t) => ({ key: t, label: ZONE_TYPE_LABEL[t] }))].map(({ key, label }) => (
            <button key={key} onClick={() => setTypeFilter(key)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${typeFilter === key ? 'bg-brand-cyan text-brand-midnight' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`}>
              {label}
            </button>
          ))}
        </div>

        {/* Zone cards */}
        {zones.length === 0 ? (
          <p className="text-sm text-gray-400 py-4">Nema zona. {isAdmin && 'Kliknite "+ Nova zona" da dodate prvu.'}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {zones.map((z) => (
              <div key={z.id} className={`bg-white rounded-xl border p-5 space-y-3 transition-shadow hover:shadow-md ${!z.active ? 'opacity-60' : ''}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{z.name}</p>
                    {z.description && <p className="text-xs text-gray-500 mt-0.5 truncate">{z.description}</p>}
                  </div>
                  <Badge className={`shrink-0 ${ZONE_TYPE_COLOR[z.zone_type] ?? 'bg-gray-100 text-gray-700'}`}>
                    {ZONE_TYPE_LABEL[z.zone_type] ?? z.zone_type}
                  </Badge>
                </div>

                {editing?.id === z.id ? (
                  <ZoneForm
                    form={form}
                    setForm={setForm}
                    formError={formError}
                    onSave={() => { if (!form.name.trim()) { setFormError('Naziv je obavezan'); return }; updateMut.mutate(z) }}
                    onCancel={() => setEditing(null)}
                    saving={updateMut.isPending}
                  />
                ) : (
                  <>
                    <p className="text-xs text-gray-400">
                      <svg className="w-3.5 h-3.5 inline mr-1 -mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      {z.meter_count} {z.meter_count === 1 ? 'vodomer' : 'vodomera'}
                    </p>
                    {isAdmin && (
                      <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                        <button onClick={() => openEdit(z)} className="text-xs text-brand-cyan-text hover:text-brand-midnight font-medium">Uredi</button>
                        <button onClick={() => toggleActive.mutate(z)} className="text-xs text-gray-500 hover:text-gray-700 font-medium">
                          {z.active ? 'Deaktiviraj' : 'Aktiviraj'}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Main page ───────────────────────────────────────────────────────────────

export default function ScadaView() {
  const qc = useQueryClient()
  const token = useAuthStore((s) => s.token)
  const { user } = useAuthStore()
  const isAdmin = user?.roles.some((r) => ['admin', 'company_admin'].includes(r)) ?? false

  const [simulatePending, setSimulatePending] = useState(false)
  const { openDeviceDetail, deviceDetailModal } = useDeviceDetailModal()

  async function handleSimulate(deviceKey: string, alertType: 'pump_offline' | 'low_reservoir') {
    if (simulatePending) return
    setSimulatePending(true)
    try {
      await scadaApi.simulateAlert(deviceKey, alertType)
      alert('Test alarm poslat na vaš telefon.')
    } catch (e: unknown) {
      alert(apiError(e, 'Greška pri slanju test alarma.'))
    } finally {
      setSimulatePending(false)
    }
  }

  const { data, isLoading } = useQuery({
    queryKey: ['scada-latest'],
    queryFn:  () => scadaApi.latest().then((r) => r.data),
    refetchInterval: 30_000,
  })
  // /scada/latest doesn't carry alerts_muted — same devices list every
  // other section on this page already fetches under this queryKey, so
  // this reuses that cache rather than adding a real second request.
  const { data: devicesForMute = [] } = useQuery({
    queryKey: ['scada-devices'],
    queryFn:  () => scadaApi.listDevices().then((r) => r.data),
    staleTime: 60_000,
  })
  const mutedByKey = new Map(devicesForMute.map((d) => [d.device_key, d.alerts_muted]))
  // The live event log below (handleEvent) needs device_key -> label too,
  // but it's a useCallback with empty deps (so the SSE connection in the
  // effect further down doesn't tear down/reopen every time this devices
  // list happens to refetch) — a ref lets it read the current map without
  // being a dependency.
  const labelByKeyRef = useRef<Map<string, string>>(new Map())
  useEffect(() => {
    labelByKeyRef.current = new Map(devicesForMute.map((d) => [d.device_key, d.label]))
  }, [devicesForMute])
  // Standalone sensors (flow meters, pressure sensors, level sensors,
  // valves) had no card anywhere on this dashboard -- reported live: an
  // attached flow meter was nowhere to be found near the two reservoirs its
  // pipe connects. Cross-referenced to whichever pipe it's on, same pattern
  // UredajiGrid's own pipeLabelByDeviceId already uses.
  const { data: zoneLinksForOtherDevices = [] } = useQuery({
    queryKey: ['scada-zone-links'],
    queryFn:  () => scadaApi.listZoneLinks().then((r) => r.data),
    staleTime: 60_000,
  })
  const pipeLabelByDeviceId = new Map<string, string>()
  zoneLinksForOtherDevices.forEach((lk) => lk.path.forEach((p) => {
    if (p.point_type === 'device' && p.device_id) pipeLabelByDeviceId.set(p.device_id, lk.label)
  }))
  const otherDevices = devicesForMute
    .filter((d) => d.active && d.node_type !== 'pump' && d.node_type !== 'reservoir')
    .sort((a, b) => a.node_type.localeCompare(b.node_type) || a.label.localeCompare(b.label))
  // GET /setup/edge-nodes is company_admin/admin-only server-side today —
  // this page is also reachable by scada_operator/scada_repair/dispatcher, who'd 403.
  // Gate the fetch itself rather than let it fail silently in the console.
  const { data: edgeNodes = [] } = useQuery({
    queryKey: ['scada-edge-nodes'],
    queryFn:  () => scadaApi.listEdgeNodes().then((r) => r.data),
    refetchInterval: 30_000,
    enabled: isAdmin,
  })

  const [livePumps, setLivePumps]           = useState<Map<string, Partial<PumpStatus>>>(new Map())
  const [liveReservoirs, setLiveReservoirs] = useState<Map<string, Partial<ReservoirStatus>>>(new Map())
  const [log, setLog]                       = useState<LogEntry[]>([])
  const logRef = useRef<LogEntry[]>([])

  const [pumpHistories, setPumpHistories]           = useState<Map<string, PumpHistoryPoint[]>>(new Map())
  const [reservoirHistories, setReservoirHistories] = useState<Map<string, ReservoirHistoryPoint[]>>(new Map())

  useEffect(() => {
    if (!data) return
    data.pumps.forEach((p) => {
      scadaApi.pumpHistory(p.device_key, 6).then((r) =>
        setPumpHistories((prev) => new Map(prev).set(p.device_key, r.data))
      )
    })
    data.reservoirs.forEach((r) => {
      scadaApi.reservoirHistory(r.device_key, 6).then((res) =>
        setReservoirHistories((prev) => new Map(prev).set(r.device_key, res.data))
      )
    })
  }, [data])

  const handleEvent = useCallback((event: ScadaEvent) => {
    if (event.type === 'pump') {
      setLivePumps((prev) => new Map(prev).set(event.device_key, {
        active: event.active, recorded_at: event.recorded_at,
      }))
      setPumpHistories((prev) => {
        const hist = [...(prev.get(event.device_key) ?? []), {
          active: event.active, flow_lps: event.flow_lps, pressure_kpa: null, recorded_at: event.recorded_at,
        }].slice(-1440)
        return new Map(prev).set(event.device_key, hist)
      })
      const entry: LogEntry = {
        id: `${event.device_key}-${Date.now()}`, type: 'pump',
        text: `${labelByKeyRef.current.get(event.device_key) ?? event.device_key}: ${event.active ? 'pokrenuta' : 'zaustavljena'}`,
        time: fmtTime(event.recorded_at),
      }
      logRef.current = [entry, ...logRef.current].slice(0, 50)
      setLog([...logRef.current])
    } else if (event.type === 'reservoir') {
      setLiveReservoirs((prev) => new Map(prev).set(event.device_key, {
        depth_m: event.depth_m, recorded_at: event.recorded_at,
      }))
      setReservoirHistories((prev) => {
        const hist = [...(prev.get(event.device_key) ?? []), {
          depth_m: event.depth_m, volume_m3: event.volume_m3, recorded_at: event.recorded_at,
        }].slice(-1440)
        return new Map(prev).set(event.device_key, hist)
      })
      const entry: LogEntry = {
        id: `${event.device_key}-${Date.now()}`, type: 'reservoir',
        text: `${labelByKeyRef.current.get(event.device_key) ?? event.device_key}: ${event.depth_m.toFixed(3)} m`,
        time: fmtTime(event.recorded_at),
      }
      logRef.current = [entry, ...logRef.current].slice(0, 50)
      setLog([...logRef.current])
    }
    // flow events are ignored in the live view for now
  }, [])

  useEffect(() => {
    if (!token) return
    const cleanup = openScadaStream(token, handleEvent)
    return () => cleanup()
  }, [token, handleEvent])

  // A real tab suspension (backgrounded long enough for the browser to
  // freeze/throttle timers) can defeat both refetchInterval and React
  // Query's default focus-refetch — visibilitychange is the one signal
  // that reliably fires the moment the tab is actually live again, so
  // force these two queries fresh rather than trust the heuristics alone.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        qc.invalidateQueries({ queryKey: ['scada-latest'] })
        qc.invalidateQueries({ queryKey: ['scada-devices'] })
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [qc])

  const pumps = (data?.pumps ?? []).map((p) => ({ ...p, ...(livePumps.get(p.device_key) ?? {}) }))
  const reservoirs = (data?.reservoirs ?? []).map((r) => ({ ...r, ...(liveReservoirs.get(r.device_key) ?? {}) }))

  const alertingReservoirs = reservoirs.filter(
    (r) => r.status_severity === 'critical' || r.status_severity === 'warning'
  )
  const bannerCritical = alertingReservoirs.some((r) => r.status_severity === 'critical')

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-8 h-8 rounded-full border-[3px] border-brand-cyan border-t-transparent animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-400">Učitavanje SCADA podataka…</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-(--breakpoint-xl) mx-auto p-6 space-y-6">

      {/* Status bar */}
      <div className="flex items-center justify-end gap-3 flex-wrap">
        {edgeNodes.map((node) => <EdgeServerBadge key={node.id} node={node} />)}
      </div>

      {/* Reservoir alarm banner — severity is the backend's call. What's
          measured and alerted on is the reservoir's water level, not
          pressure -- pressure is a possible downstream consequence for a
          gravity-fed tower, not what triggered this. Reported live: the
          old "Upozorenje o pritisku" header was wrong for a reservoir
          that's simply out of its configured level range for any reason
          (e.g. too full, not just draining toward empty). "Do kritičnog
          nivoa" matches DeviceDetailModal's own wording for the same
          countdown, so the two surfaces agree. */}
      {alertingReservoirs.length > 0 && (
        <div className={`p-4 rounded-xl border ${bannerCritical ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200'}`}>
          <p className={`font-semibold text-sm ${bannerCritical ? 'text-brand-critical-text' : 'text-brand-warning-text'}`}>
            ⚠ Upozorenje o nivou rezervoara —{' '}
            {alertingReservoirs.map((r) => (
              <span key={r.device_key}>
                {r.label}:{' '}
                {r.minutes_to_critical !== null
                  ? <><strong>{fmtCountdown(r.minutes_to_critical)}</strong> do kritičnog nivoa</>
                  : <strong>nivo van dozvoljenog opsega</strong>}
                {' '}
              </span>
            ))}
          </p>
        </div>
      )}

      {/* Pumps */}
      {pumps.length > 0 && (
        <section>
          <h2 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-3">
            Crpne stanice ({pumps.filter((p) => p.active).length}/{pumps.length} aktivne)
          </h2>
          <div className="grid gap-5 grid-cols-[repeat(auto-fill,minmax(300px,1fr))]">
            {pumps.map((p) => (
              <PumpCard key={p.device_key} pump={p} history={pumpHistories.get(p.device_key) ?? []}
                muted={mutedByKey.get(p.device_key) ?? false}
                onSimulate={isAdmin ? () => handleSimulate(p.device_key, 'pump_offline') : undefined}
                onOpenDetail={() => openDeviceDetail(p.device_key, 'pump')} />
            ))}
          </div>
        </section>
      )}

      {/* Reservoirs */}
      {reservoirs.length > 0 && (
        <section>
          <h2 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-3">Rezervoari</h2>
          <div className="grid gap-5 grid-cols-[repeat(auto-fill,minmax(340px,1fr))]">
            {reservoirs.map((r) => (
              <ReservoirCard key={r.device_key} res={r} history={reservoirHistories.get(r.device_key) ?? []}
                muted={mutedByKey.get(r.device_key) ?? false}
                onSimulate={isAdmin ? () => handleSimulate(r.device_key, 'low_reservoir') : undefined}
                onOpenDetail={() => openDeviceDetail(r.device_key, 'reservoir')} />
            ))}
          </div>
        </section>
      )}

      {/* Other devices — flow meters, pressure sensors, level sensors,
          valves. No card section of their own before this: a flow meter
          attached to a pipe was reachable nowhere on this page. */}
      {otherDevices.length > 0 && (
        <section>
          <h2 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-3">Ostali uređaji</h2>
          <div className="grid gap-5 grid-cols-[repeat(auto-fill,minmax(280px,1fr))]">
            {otherDevices.map((d) => (
              <GenericDeviceCard key={d.device_key} device={d}
                pipeLabel={pipeLabelByDeviceId.get(d.id) ?? null}
                onOpenDetail={() => openDeviceDetail(d.device_key, d.node_type)} />
            ))}
          </div>
        </section>
      )}

      <TopologyView pumps={pumps} reservoirs={reservoirs} />

      {deviceDetailModal}

      <EventLog entries={log} />

      {isAdmin && (
        <div className="space-y-5 pt-2">
          <UredajiGrid />
          <ScadaZoneGrid />
          <AccessPointGrid />
          <CevovodiGrid />
          <NadzorPlovkaGrid />
        </div>
      )}

      {user?.modules?.includes('meter_reading') && <ZonesSection />}
    </div>
  )
}
